"use client";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { ChevronDown, Filter, ChevronRight } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useAtom, useSetAtom } from "jotai";
import type { FilterSectionResult } from "common/types/search";
import {
  useI18N,
  useSearchPageParams,
  useFilters,
  usePostRecentSearch,
  type SavedSearch,
  type SelectedLocation,
} from "common/hooks";
import { cn } from "@/lib/utils";
import {
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  savedPlaceSortByToViewAtom,
  searchTermInputAtom,
  mapCenterAtom,
  pendingGooglePlaceIdAtom,
  fitMapToResultsAtom,
  locationSearchAnchorAtom,
  filterPanelRequestAtom,
} from "@/state/atoms";
import { AccessibilityFilterPanel } from "./FilterPannel/Accessibility";
import {
  BusinessTypeDropdown,
  type BusinessType,
} from "./FilterPannel/BusinessType";
import {
  OpeningHoursFilterPanel,
  type OpeningHoursMode,
} from "./FilterPannel/OpeningHours";
import AutoSuggestInput from "@/components/Search/AutoSuggestInput";
import FilterChip from "@/components/Search/FilterChip";
import {
  Suggestion,
  RecentSearchResult,
  NeighbourhoodSuggestionResult,
  getFirstResultLocationAndAddress,
  resolveSuggestionArea,
  closeSuggestSession,
  type UnifiedSuggestItem,
} from "common/apis";
import { trackEvent } from "common/analytics";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/shadcn/components/ui/accordion";
import {
  bucketUrlPrefix,
  defaultRegion,
  enableGooglePoiClick,
} from "common/config";
import { isFocusDisplayTerm } from "common/utils/focus";
import { encodeSelection, tagIdsOf, type SelectionKey } from "common/utils/accessibilitySelection";
import { sameSelection, selectionOf, withSelection } from "@/lib/accessibilityFilterState";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useAccessibilitySelectionCount } from "@/hooks/useAccessibilitySelectionCount";
import { savedFiltersAppliedAtom } from "@/store/atoms";
import ChangeLocationModal from "@/components/HomePage/ChangeLocationModal/ChangeLocationModal";
import SearchLocation from "@/components/Search/SearchLocation";
import ScrollContainer from "react-indiana-drag-scroll";
import { Combobox, ComboboxOption } from "@/shadcn/components/ui/combobox";
import { Sheet, SheetContent, SheetTitle } from "@/shadcn/components/ui/sheet";
import AccessibilityFeaturesContextAware from "../FiltersSheet/components/AccessibilityFeaturesContextAware";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  SlidersHorizontalIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
} from "@phosphor-icons/react";

// Type for neighborhood from the API
type NeighborhoodSuggestion = NeighbourhoodSuggestionResult["searches"][0];

// ============================================================================
// Types
// ============================================================================

// Re-export SavedSearch type from common hooks for backward compatibility
export type { SavedSearch };

// Active panel type for internal state
type ActivePanel =
  | "none"
  | "savedSearches"
  | "suggestions"
  | "accessibility"
  | "businessType"
  | "openingHours";

// Filter state passed on search submission
export interface SearchFilters {
  searchTerm: string;
  businessTypes: string[];
  accessibilityOptions: SelectionKey[];
  openingHours: {
    mode: OpeningHoursMode;
    day: string;
    time: string;
  };
}

// Canonical search_method (tracking-data vocab) for an autosuggest tap, by suggestion kind. Per the
// spec, suggestion taps fall under typed_input except brand and location (a neighbourhood), which promote.
function suggestionSearchMethod(type?: string): string {
  if (type === "brand") return "brand";
  if (type === "location") return "neighborhood";
  return "typed_input";
}

export interface SearchBarProps {
  /** Current search term value */
  searchTerm: string;
  /** Callback when search term changes (only used when inputMode="input") */
  onSearchChange?: (value: string) => void;
  /** Callback when search input is focused/clicked */
  onSearchFocus?: () => void;
  /** Callback when search is submitted (Enter key or button click) */
  onSearchSubmit: () => void;
  /** Callback when search is submitted with all filters - receives complete filter state */
  onSearchWithFilters?: (filters: SearchFilters) => void;
  /** Callback to perform search with specific parameters (used by search route) */
  onPerformSearch?: (
    text: string,
    searchType: string | null,
    searchValue: string | null,
    latitude: number | null,
    longitude: number | null,
    address: string | null,
    // canonical entry-path search_method (tracking-data vocab); preserved through performSearch
    searchMethod?: string | null,
  ) => void;
  /** Callback when clear button is clicked (only used when inputMode="input") */
  onClearSearch?: () => void;
  /** Number of saved searches to display */
  savedSearchesCount: number;
  /** Array of saved searches to display in dropdown */
  savedSearches?: SavedSearch[];
  /** Callback when saved searches button is clicked */
  onSavedSearchesClick: () => void;
  /** Whether saved searches dropdown is open (for chevron rotation) */
  isSavedSearchesOpen?: boolean;
  /** Callback when a saved search is selected */
  onSavedSearchSelect?: (search: SavedSearch) => void;
  /** Number of accessibility filters applied */
  accessibilityCount?: number;
  /** Number of business type filters applied */
  businessTypeCount?: number;
  /** Callback when accessibility filter is clicked */
  onAccessibilityClick?: () => void;
  /** Callback when business type filter is clicked */
  onBusinessTypeClick?: () => void;
  /** Callback when opening hour filter is clicked */
  onOpeningHourClick?: () => void;
  /** Input mode -"input" for real text input,"button" for clickable button */
  inputMode?: "input" | "button";
  /** Whether to show the container wrapper with padding */
  showContainer?: boolean;
  /** Custom container className */
  containerClassName?: string;

  // ============================================================================
  // Accessibility Filter Props
  // ============================================================================
  /** Currently selected accessibility options (number IDs) */
  selectedAccessibilityOptions?: SelectionKey[];
  /** Callback when accessibility options change */
  onAccessibilityOptionsChange?: (keys: SelectionKey[]) => void;
  /** Callback when clear all accessibility filters */
  onAccessibilityClearAll?: () => void;

  // ============================================================================
  // Business Type Props
  // ============================================================================
  /** Optional override for business types (uses useFilters hook by default) */
  businessTypes?: BusinessType[];
  /** Currently selected business types */
  selectedBusinessTypes?: string[];
  /** Callback when business type selection changes */
  onBusinessTypesChange?: (selectedTypes: string[]) => void;
  /** Callback when a single business type is toggled */
  onBusinessTypeToggle?: (typeName: string) => void;

  // ============================================================================
  // Opening Hours Props
  // ============================================================================
  /** Current opening hours mode */
  openingHoursMode?: OpeningHoursMode;
  /** Callback when opening hours mode changes */
  onOpeningHoursModeChange?: (mode: OpeningHoursMode) => void;
  /** Selected day for custom opening hours */
  selectedDay?: string;
  /** Callback when day changes */
  onDayChange?: (day: string) => void;
  /** Selected time for custom opening hours */
  selectedTime?: string;
  /** Callback when time changes */
  onTimeChange?: (time: string) => void;

  // ============================================================================
  // AutoSuggest Props
  // ============================================================================
  /** Callback when a suggestion is selected from autosuggest */
  onSelectSuggestion?: (suggestion: Suggestion) => void;
  /** Latitude for location-based suggestions */
  latitude?: number;
  /** Longitude for location-based suggestions */
  longitude?: number;
  /** Radius in km for location-based suggestions */
  radiusKm?: number;
  /** Tagging mode filter for suggestions */
  tagging_mode?: string;
  /** Business types filter for suggestions */
  types?: string;
  /** Accessibilities filter for suggestions */
  accessibilities?: string;
  /** `heading:tag|…` pairs for the suggestions request (DEV-600) */
  accessibilitySections?: string;
  /** Show venue options filter for suggestions */
  showVenueOptions?: string;

  // ============================================================================
  // Search Suggestions Props
  // ============================================================================
  /** Whether to show the SearchSuggestionsDropdown when input is empty/focused */
  showSearchSuggestions?: boolean;
  /** Callback when nearby places is clicked */
  onNearbyPlacesClick?: () => void;
  /** Callback when a recent search is selected */
  onRecentSearchSelect?: (search: RecentSearchResult) => void;
  /** Callback when a neighborhood is selected */
  onNeighborhoodSelect?: (neighborhood: NeighborhoodSuggestion) => void;
  /** Callback when login button is pressed (for non-logged in users viewing recent searches) */
  onLoginPress?: () => void;

  // ============================================================================
  // Layout Props
  // ============================================================================
  /** Layout mode - determines positioning and wrapper behavior */
  layout?: "default" | "header" | "overlay" | "overlaymobile";

  /** For overlay mode: whether the overlay is open */
  isOverlayOpen?: boolean;

  /** For overlay mode: callback when overlay should close */
  onOverlayClose?: () => void;

  /**
   * A suggestion picked somewhere else — the home page's change-location
   * modal, searching in place — for this bar to run exactly as if it had been
   * picked from its own list. `id` changes per pick.
   */
  pendingSuggestion?: {
    id: number;
    item: UnifiedSuggestItem;
    sessionToken: string;
  } | null;

  /** For overlay mode: callback when search is submitted with term and filter count */
  onOverlaySearch?: (searchTerm: string, filterCount: number) => void;

  /** For overlay mode: initial search term to populate the input */
  initialSearchTerm?: string;

  /** For header mode: whether to show loading skeleton */
  isLoading?: boolean;

  /**
   * Search page variant of the header bar: filters sit in a row below the input
   * and apply as soon as their panel closes, so there is no Search button for
   * them. Drops the location and recent-searches rows.
   */
  filtersBelowInput?: boolean;

  // ============================================================================
  // Location Props (for header layout)
  // ============================================================================
  /** Location text to display */
  locationText?: string;
  /**
   * Home's header shows the city row from the DEV-509 frame; every other
   * surface keeps the original inline row - @see `SearchLocation`.
   */
  locationVariant?: "inline" | "city";
  /** Second line for the `city` variant. */
  locationSubtitle?: string;
  /**
   * Marks the search input row as a coach-mark target - @see `HomeTour`. On
   * the input rather than the whole bar, whose sticky header starts at the top
   * of the viewport and leaves a bubble placed "above" it nowhere to go.
   */
  tourAnchor?: string;
  /**
   * The field on its own: no filter dropdowns, no saved/recent row, no sticky
   * header around it, and a plain magnifier instead of the filled search
   * button.
   *
   * Home's search bar in the DEV-509 frame, which sits inside the carousel
   * column beside the map rather than in a page header. Filtering happens on
   * `/search`, whose own header instance is untouched by this.
   *
   * Recent searches, nearby places and neighbourhoods still reach the user -
   * they are in `AutoSuggestInput`'s dropdown, not in the row that goes.
   */
  simplified?: boolean;
  /**
   * With `simplified`: draw the suggestion lists into this element instead of
   * as dropdowns - @see `AutoSuggestInput`'s prop of the same name, and
   * `SearchModal`, which is what uses it.
   */
  inlineSuggestionsContainer?: HTMLElement | null;
  /**
   * Filled with this bar's submit, for a Search button that lives outside it -
   * `SearchModal`'s bottom bar. Runs exactly what Enter or the magnifier runs.
   */
  submitRef?: React.MutableRefObject<(() => void) | null>;
  /**
   * With `simplified`: true while a submit or a picked suggestion is still
   * working before it navigates (a geocode, a place lookup), false once it has
   * settled. Lets a host show that something is happening - `SearchModal`.
   */
  onBusyChange?: (busy: boolean) => void;
  /** Callback when location is clicked to change */
  onLocationChange?: (location: SelectedLocation) => void;
  /** Whether the location is the default location */
  isDefaultLocation?: boolean;
  /** Latitude for location */
  locationLatitude?: number;
  /** Longitude for location */
  locationLongitude?: number;
  /** Current location text (for copying from home page to search page) */
  currentLocationText?: string;

  // ============================================================================
  // Recent Searches Props (for header layout)
  // ============================================================================
  /** Array of recent search results to display as buttons */
  recentSearches?: RecentSearchResult[];
  /** Callback when Use Precise Location is clicked */
  onUsePreciseLocationClick?: () => void;
  /** Whether the user is logged in */
  isLoggedIn?: boolean;

  // ============================================================================
  // Accessibility Tags Data (for header layout)
  // ============================================================================
  /** Optional accessibility tags data for header layout (desktop home page) */
  accessibilityTagsData?: any[];
  /** The search's own filter sections: the panel prefers them; analytics names keep accessibilityTagsData. */
  filterSectionsData?: FilterSectionResult[];
}

// ============================================================================
// Component
// ============================================================================

export const SearchBar = ({
  searchTerm: externalSearchTerm,
  onSearchChange,
  onSearchFocus,
  onSearchSubmit,
  onSearchWithFilters,
  onPerformSearch,
  onClearSearch,
  savedSearchesCount,
  savedSearches = [],
  onSavedSearchesClick,
  isSavedSearchesOpen = false,
  onSavedSearchSelect,
  accessibilityCount,
  businessTypeCount,
  onAccessibilityClick,
  onBusinessTypeClick,
  onOpeningHourClick,
  inputMode = "input",
  showContainer = true,
  containerClassName,
  // Accessibility filter props
  selectedAccessibilityOptions = [],
  onAccessibilityOptionsChange,
  onAccessibilityClearAll,
  // Business type props
  businessTypes,
  selectedBusinessTypes = [],
  onBusinessTypesChange,
  onBusinessTypeToggle,
  // Opening hours props
  openingHoursMode = "any",
  onOpeningHoursModeChange,
  selectedDay = "Tuesday",
  onDayChange,
  selectedTime = "10:00 AM",
  onTimeChange,
  // AutoSuggest props
  onSelectSuggestion,
  latitude = 0,
  longitude = 0,
  radiusKm = 10,
  tagging_mode,
  types,
  accessibilities,
  accessibilitySections,
  showVenueOptions,
  // Search suggestions props
  showSearchSuggestions = true,
  onNearbyPlacesClick,
  onRecentSearchSelect,
  onNeighborhoodSelect,
  onLoginPress,
  // Layout props
  layout = "default",
  isOverlayOpen = true,
  onOverlayClose,
  pendingSuggestion,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onOverlaySearch, // Kept for API compatibility
  initialSearchTerm = "",
  isLoading = false,
  filtersBelowInput = false,
  // Location props
  locationText,
  locationVariant = "inline",
  locationSubtitle,
  tourAnchor,
  simplified = false,
  inlineSuggestionsContainer,
  submitRef,
  onBusyChange,
  onLocationChange,
  isDefaultLocation = false,
  locationLatitude,
  locationLongitude,
  currentLocationText,
  // Recent searches props
  recentSearches = [],
  onUsePreciseLocationClick,
  isLoggedIn = false,
  // Accessibility tags data
  accessibilityTagsData,
  filterSectionsData,
}: SearchBarProps) => {
  const i18n = useI18N();
  const navigate = useNavigate();
  const { searchPageParams, setSearchPageParams } = useSearchPageParams();
  const [mapCenter, setMapCenter] = useAtom(mapCenterAtom);
  const {
    businessTypes: hookBusinessTypes,
    filtersState,
    setFiltersState,
  } = useFilters();
  const isDark = useDarkMode();
  const { mutate: addRecentSearch } = usePostRecentSearch();
  const { setSelectedVenue } = useSelectedVenue();
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [, setSavedPlaceSortByToView] = useAtom(savedPlaceSortByToViewAtom);
  const setPendingGooglePlaceId = useSetAtom(pendingGooglePlaceIdAtom);
  const setFitMapToResults = useSetAtom(fitMapToResultsAtom);
  const setLocationSearchAnchor = useSetAtom(locationSearchAnchorAtom);
  // Counts picks, so an awaited one can tell it was superseded.
  const selectionSeqRef = useRef(0);

  // Helper to ensure we never pass NaN coordinates to API.
  // On the search page (overlay/overlaymobile layouts) mapCenter tracks the live
  // map viewport and takes priority.  On the home page (header layout) there is no
  // map, so mapCenterAtom holds stale search-page coordinates; use the explicit
  // latitude/longitude props instead (fed from desktopHomePageSearchParams).
  const getSafeCoordinates = useCallback(() => {
    if (layout !== "header") {
      if (
        mapCenter.lat &&
        mapCenter.lng &&
        !isNaN(mapCenter.lat) &&
        isFinite(mapCenter.lat) &&
        !isNaN(mapCenter.lng) &&
        isFinite(mapCenter.lng)
      ) {
        return { lat: mapCenter.lat, lng: mapCenter.lng };
      }
    }

    // Fallback to props (always correct on the home page)
    const safeLat =
      !isNaN(latitude) && isFinite(latitude)
        ? latitude
        : defaultRegion.latitude;
    const safeLng =
      !isNaN(longitude) && isFinite(longitude)
        ? longitude
        : defaultRegion.longitude;
    return { lat: safeLat, lng: safeLng };
  }, [layout, mapCenter.lat, mapCenter.lng, latitude, longitude]);

  // Constants for opening hours
  const DAYS_OF_WEEK = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const TIME_OPTIONS = [
    "12:00 AM",
    "12:30 AM",
    "1:00 AM",
    "1:30 AM",
    "2:00 AM",
    "2:30 AM",
    "3:00 AM",
    "3:30 AM",
    "4:00 AM",
    "4:30 AM",
    "5:00 AM",
    "5:30 AM",
    "6:00 AM",
    "6:30 AM",
    "7:00 AM",
    "7:30 AM",
    "8:00 AM",
    "8:30 AM",
    "9:00 AM",
    "9:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "12:00 PM",
    "12:30 PM",
    "1:00 PM",
    "1:30 PM",
    "2:00 PM",
    "2:30 PM",
    "3:00 PM",
    "3:30 PM",
    "4:00 PM",
    "4:30 PM",
    "5:00 PM",
    "5:30 PM",
    "6:00 PM",
    "6:30 PM",
    "7:00 PM",
    "7:30 PM",
    "8:00 PM",
    "8:30 PM",
    "9:00 PM",
    "9:30 PM",
    "10:00 PM",
    "10:30 PM",
    "11:00 PM",
    "11:30 PM",
  ];

  // Get business types
  const businessTypesList = businessTypes || hookBusinessTypes || [];
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs for filter trigger buttons (for focus management)
  const accessibilityButtonRef = useRef<HTMLButtonElement>(null);
  const businessTypeButtonRef = useRef<HTMLButtonElement>(null);
  const openingHoursButtonRef = useRef<HTMLButtonElement>(null);
  const accessibilityWasOpenRef = useRef(false);
  const businessTypeWasOpenRef = useRef(false);
  const openingHoursWasOpenRef = useRef(false);
  // Dedupes filter_closed within one open-session (reset when a panel opens) so the panel's onClose
  // (Escape / click-outside / trigger re-click) and the toggle handler can't double-fire.
  const filterClosedFiredRef = useRef(false);

  // Track which button triggered panel opening (for focus return)
  const lastTriggerButtonRef = useRef<HTMLButtonElement | null>(null);

  // Refs for recent search pills (for keyboard navigation)
  const recentSearchRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Ref for location button (for focus return from modal)
  const locationButtonRef = useRef<HTMLButtonElement>(null);

  // Internal state for which panel is open
  const [activePanel, setActivePanel] = useState<ActivePanel>("none");
  const [filterPanelRequest, setFilterPanelRequest] = useAtom(
    filterPanelRequestAtom,
  );

  // Accordion state for mobile filters
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    null,
  );

  // State for filters container visibility
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // State for opening hours dropdowns in accordion
  const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);

  // State for segmented control (New search vs Saved searches)
  const [activeSegment, setActiveSegment] = useState<"new" | "saved">("new");

  // State for screen reader announcements
  const [statusMessage, setStatusMessage] = useState("");

  // Internal state for accessibility filters if not controlled
  const [internalAccessibilityOptions, setInternalAccessibilityOptions] =
    useState<SelectionKey[]>([]);

  // Internal state for business types if not controlled
  const [internalSelectedBusinessTypes, setInternalSelectedBusinessTypes] =
    useState<string[]>([]);

  // Internal state for opening hours if not controlled
  const [internalOpeningHoursMode, setInternalOpeningHoursMode] =
    useState<OpeningHoursMode>("any");
  const [internalSelectedDay, setInternalSelectedDay] = useState("Tuesday");
  const [internalSelectedTime, setInternalSelectedTime] = useState("10:00 AM");

  // Internal state for search term (used in header/overlay layouts)
  const [internalSearchTerm, setInternalSearchTerm] =
    useAtom(searchTermInputAtom);

  // Internal state for saved searches open (header layout)
  const [internalSavedSearchesOpen, setInternalSavedSearchesOpen] =
    useState(false);

  // Internal state for change location modal
  const [isChangeLocationModalOpen, setIsChangeLocationModalOpen] =
    useState(false);

  // Determine which searchTerm to use based on layout
  const searchTerm =
    layout === "header" || layout === "overlay" || layout === "overlaymobile"
      ? internalSearchTerm
      : externalSearchTerm;

  // Reset saved place view state on mount
  useEffect(() => {
    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");
    setSavedPlaceSortByToView("");
  }, [
    setSavedPlaceIdToView,
    setSavedPlaceByUserIdToView,
    setSavedPlaceSortByToView,
  ]);

  // Sync internal search term with initialSearchTerm for overlay mode
  useEffect(() => {
    if (layout === "overlay" || layout === "overlaymobile") {
      setInternalSearchTerm(initialSearchTerm);
    }
  }, [initialSearchTerm, layout, setInternalSearchTerm]);

  // Sync internal accessibility options with props for overlay and header modes
  useEffect(() => {
    if (
      layout === "overlay" ||
      layout === "overlaymobile" ||
      layout === "header"
    ) {
      setInternalAccessibilityOptions((prev) => {
        // Compare arrays to avoid infinite update loops from default prop values
        if (
          prev.length === selectedAccessibilityOptions.length &&
          prev.every((v, i) => v === selectedAccessibilityOptions[i])
        ) {
          return prev;
        }
        return selectedAccessibilityOptions;
      });
    }
  }, [selectedAccessibilityOptions, layout]);

  // Sync internal business types with props for overlay and header modes
  useEffect(() => {
    if (
      layout === "overlay" ||
      layout === "overlaymobile" ||
      layout === "header"
    ) {
      setInternalSelectedBusinessTypes((prev) => {
        // Compare arrays to avoid infinite update loops from default prop values
        if (
          prev.length === selectedBusinessTypes.length &&
          prev.every((v, i) => v === selectedBusinessTypes[i])
        ) {
          return prev;
        }
        return selectedBusinessTypes;
      });
    }
  }, [selectedBusinessTypes, layout]);

  // Sync filtersState.accessibilityOptions to internal state for overlay and mobile modes
  // This ensures that changes made by AccessibilityFeaturesContextAware are reflected in SearchBar
  useEffect(() => {
    if (
      (layout === "overlay" ||
        layout === "overlaymobile" ||
        layout === "header") &&
      filtersState.accessibilityOptions
    ) {
      const keys = selectionOf(filtersState);
      setInternalAccessibilityOptions((prev) =>
        sameSelection(prev, keys) ? prev : keys,
      );
    }
  }, [filtersState, layout]);

  // Clear status message after announcement (for screen readers)
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage("");
      }, 3000); // Clear after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  // Sync internal opening hours state with props for overlay and header modes
  useEffect(() => {
    if (
      layout === "overlay" ||
      layout === "overlaymobile" ||
      layout === "header"
    ) {
      setInternalOpeningHoursMode((prev) => {
        if (openingHoursMode && openingHoursMode !== prev) {
          return openingHoursMode;
        }
        return prev;
      });
      setInternalSelectedDay((prev) => {
        if (selectedDay && selectedDay !== prev) {
          return selectedDay;
        }
        return prev;
      });
      setInternalSelectedTime((prev) => {
        if (selectedTime && selectedTime !== prev) {
          return selectedTime;
        }
        return prev;
      });
    }
  }, [openingHoursMode, selectedDay, selectedTime, layout]);

  // Handle escape key to close overlay
  useEffect(() => {
    if ((layout !== "overlay" && layout !== "overlaymobile") || !isOverlayOpen)
      return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onOverlayClose?.();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [layout, isOverlayOpen, onOverlayClose]);

  // The mobile sheet ticks from the shared filter state, which a URL-only search (home's quick
  // action) never fills, so each open starts from the applied search.
  const wasOverlayOpenRef = useRef(false);
  const setSavedFiltersApplied = useSetAtom(savedFiltersAppliedAtom);
  useEffect(() => {
    const opened = isOverlayOpen && !wasOverlayOpenRef.current;
    wasOverlayOpenRef.current = isOverlayOpen;
    if (layout !== "overlaymobile" || !opened) return;
    if (sameSelection(selectionOf(filtersState), selectedAccessibilityOptions)) return;
    setFiltersState((prev) => withSelection(prev, selectedAccessibilityOptions));
    // The dropped ticks include any the saved-filters switch added, so it must not stay on.
    setSavedFiltersApplied(false);
  }, [layout, isOverlayOpen, selectedAccessibilityOptions, filtersState, setFiltersState, setSavedFiltersApplied]);

  // Use controlled or internal state
  // For header/overlay modes, always use internal state (which is synced from props via useEffect)
  const currentAccessibilityOptions =
    layout === "header" || layout === "overlay" || layout === "overlaymobile"
      ? internalAccessibilityOptions
      : selectedAccessibilityOptions.length > 0
        ? selectedAccessibilityOptions
        : internalAccessibilityOptions;
  const currentSelectedBusinessTypes =
    layout === "header" || layout === "overlay" || layout === "overlaymobile"
      ? internalSelectedBusinessTypes
      : selectedBusinessTypes.length > 0
        ? selectedBusinessTypes
        : internalSelectedBusinessTypes;
  const currentOpeningHoursMode =
    layout === "header" || layout === "overlay" || layout === "overlaymobile"
      ? internalOpeningHoursMode
      : (openingHoursMode ?? internalOpeningHoursMode);
  const currentSelectedDay =
    layout === "header" || layout === "overlay" || layout === "overlaymobile"
      ? internalSelectedDay
      : (selectedDay ?? internalSelectedDay);
  const currentSelectedTime =
    layout === "header" || layout === "overlay" || layout === "overlaymobile"
      ? internalSelectedTime
      : (selectedTime ?? internalSelectedTime);

  // filters_count + selected_filter (tag NAMES: business types + accessibility) for filter_* events.
  // SearchBar is shared across home / search / results, so filter_* use the trackEvent primitive.
  const buildFilterSummary = (): {
    filters_count: number;
    selected_filter: string[];
  } => {
    const accNameMap = new Map<string, string>();
    (accessibilityTagsData ?? []).forEach((o) =>
      o?.accessibility_tags?.forEach((t) =>
        accNameMap.set(String(t.id), t.name),
      ),
    );
    // Names stay tag-level (event contract); the count is per section tick.
    const accNames = tagIdsOf(currentAccessibilityOptions)
      .map((id) => accNameMap.get(String(id)))
      .filter((n): n is string => Boolean(n));
    const filters_count =
      currentSelectedBusinessTypes.length +
      currentAccessibilityOptions.length +
      (currentOpeningHoursMode && currentOpeningHoursMode !== "any" ? 1 : 0);
    return {
      filters_count,
      selected_filter: [...currentSelectedBusinessTypes, ...accNames],
    };
  };

  // Venue/area suggestion taps are dispatched by handleUnifiedSelect and never reach
  // handleSuggestionSelect, so they need their own place_search_performed call.
  const trackSuggestionSearch = (props: {
    business_id?: string;
    place_name?: string;
    search_query: string;
    search_method: string;
    search_result_type: string;
  }) => {
    const summary = buildFilterSummary();
    trackEvent("place_search_performed", {
      brand_id: "",
      brand_name: "",
      business_id: props.business_id ?? "",
      place_name: props.place_name ?? "",
      filters_count: summary.filters_count,
      search_method: props.search_method,
      search_query: props.search_query,
      search_result_type: props.search_result_type,
      selected_filter: summary.selected_filter,
    });
  };

  // Close all panels
  const closeAllPanels = useCallback(() => {
    setActivePanel("none");

    // Return focus to the trigger button that opened the panel
    setTimeout(() => {
      if (lastTriggerButtonRef.current) {
        lastTriggerButtonRef.current.focus();
        lastTriggerButtonRef.current = null;
      }
    }, 0);
  }, []);

  // filter_closed = a filter dropdown dismissed WITHOUT applying (Escape / click-outside / trigger
  // re-click). Deduped via filterClosedFiredRef. NOT fired on the search/apply path, which closes via
  // closeAllPanels directly (and fires filter_applied instead).
  const fireFilterClosed = (filterType: string) => {
    if (filterClosedFiredRef.current) return;
    filterClosedFiredRef.current = true;
    trackEvent("filter_closed", {
      filter_type: filterType,
      ...buildFilterSummary(),
    });
  };
  const dismissFilterPanel = (filterType: string) => {
    fireFilterClosed(filterType);
    closeAllPanels();
  };

  // Toggle panel
  const togglePanel = useCallback((panel: ActivePanel) => {
    setActivePanel((current) => (current === panel ? "none" : panel));
  }, []);

  // Blur handler for filter panels - closes panel when focus leaves the filter wrapper
  const handleFilterPanelBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const currentTarget = event.currentTarget;

      // Use setTimeout to allow the browser to determine the new activeElement
      setTimeout(() => {
        // CheckIcon if focus moved outside the filter wrapper
        if (currentTarget && !currentTarget.contains(document.activeElement)) {
          closeAllPanels();
        }
      }, 0);
    },
    [closeAllPanels],
  );

  const handleClearAllFilters = () => {
    // clear_all_action_button -> filter_cleared (count of filters being cleared, before reset).
    trackEvent("filter_cleared", {
      filters_count: buildFilterSummary().filters_count,
    });

    closeAllPanels();
    setInternalSelectedBusinessTypes([]);
    setInternalAccessibilityOptions([]);
    setInternalOpeningHoursMode("any");
    setInternalSelectedDay("");
    setInternalSelectedTime("");
    onAccessibilityClearAll?.();

    // Clear accessibility options in global filters state
    // This is necessary for the AccessibilityFeaturesContextAware component
    setFiltersState((prev) => withSelection(prev, []));

    setSearchPageParams((prev) => ({
      ...prev,
      types: "",
      accessibilities: "",
      accessibility_sections: "",
      showVenueOptions: "All",
      openingHoursMode: "any",
      openingHoursDay: "",
      openingHoursTime: "",
    }));

    // Announce to screen readers
    setStatusMessage(i18n.get("filtersCleared") || "All filters cleared");

    // Close overlay if in overlay mode
    if (layout === "overlay" || layout === "overlaymobile") {
      onOverlayClose?.();
    }
  };

  // Map opening hours mode to the showVenueOptions param
  const showVenueOptionsForMode = (mode: string): string => {
    switch (mode) {
      case "open_now":
        return i18n.get("openNow");
      case "custom":
        return i18n.get("custom");
      default:
        return i18n.get("all");
    }
  };

  // The empty results state asks for a panel by atom, since it can't reach this
  // component's state. This chip row is CSS-hidden below `lg` (Navbar keeps it
  // mounted for layout stability), so guard on the real viewport — opening a
  // panel while hidden puts its focus-trap in a display:none subtree with no
  // tabbable node, which throws.
  useEffect(() => {
    if (!filtersBelowInput || !filterPanelRequest) return;
    if (!window.matchMedia("(min-width: 1024px)").matches) return;

    setActivePanel(filterPanelRequest);
    filterClosedFiredRef.current = false;
    setFilterPanelRequest(null);
  }, [filtersBelowInput, filterPanelRequest, setFilterPanelRequest]);

  // Below `lg` there is no chip row — the same request instead expands this
  // accordion inside the mobile search sheet (search.lazy.tsx opens the sheet
  // itself on the same atom).
  useEffect(() => {
    if (layout !== "overlaymobile" || !filterPanelRequest) return;
    if (window.matchMedia("(min-width: 1024px)").matches) return;

    setIsFiltersExpanded(true);
    setExpandedAccordion(filterPanelRequest);
    setFilterPanelRequest(null);
  }, [layout, filterPanelRequest, setFilterPanelRequest]);

  // Handle search submission with all filters
  const handleSearchSubmit = useCallback(async () => {
    closeAllPanels();

    console.log(
      "inside handleSearchSubmit layout = ",
      layout,
      "locationLatitude = ",
      locationLatitude,
      "locationLongitude = ",
      locationLongitude,
    );

    // search_submit_action_button branch -> filter_applied + place_search_performed.
    // brand_id/business_id are empty for a typed/area text search.
    const submitSummary = buildFilterSummary();
    trackEvent("filter_applied", { filter_type: "all", ...submitSummary });
    trackEvent("place_search_performed", {
      brand_id: "",
      brand_name: "",
      business_id: "",
      place_name: "",
      filters_count: submitSummary.filters_count,
      search_method: "typed_input",
      search_query: searchTerm,
      search_result_type: "place",
      selected_filter: submitSummary.selected_filter,
    });

    // Build filter state. A focused venue's name is in the box for display
    // only, so re-searching with filters keeps showing what's nearby.
    const filters: SearchFilters = {
      searchTerm: isFocusDisplayTerm(searchTerm, searchPageParams)
        ? ""
        : searchTerm,
      businessTypes: currentSelectedBusinessTypes,
      accessibilityOptions: currentAccessibilityOptions,
      openingHours: {
        mode: currentOpeningHoursMode,
        day: currentSelectedDay,
        time: currentSelectedTime,
      },
    };

    // Build filter strings for URL params
    const typesStr = filters.businessTypes.join("|");
    const { accessibilities: accessibilitiesStr, accessibility_sections: accessibilitySectionsStr } =
      encodeSelection(filters.accessibilityOptions);

    const getShowVenueOptions = showVenueOptionsForMode;

    // For keyword searches, call search-places to geocode the search term
    let geocodedLat: number | undefined;
    let geocodedLng: number | undefined;
    let geocodedAddress: string | undefined;

    if (filters.searchTerm) {
      try {
        const { lat: safeLat, lng: safeLng } = getSafeCoordinates();
        const { lat, lng, address, error } =
          await getFirstResultLocationAndAddress(
            filters.searchTerm,
            safeLat,
            safeLng,
          );

        if (!error) {
          geocodedLat = lat;
          geocodedLng = lng;
          geocodedAddress = address;
        }
      } catch (error) {
        console.error("Error geocoding search term:", error);
      }

      // Store recent search if user is logged in
      if (isLoggedIn) {
        const typesStr = filters.businessTypes.join("|");
        // Recent searches store tags only (D0-2).
        const accessibilitiesStr = encodeSelection(filters.accessibilityOptions).accessibilities;
        const showVenueOptionsValue = getShowVenueOptions(
          filters.openingHours.mode,
        );

        addRecentSearch({
          searchTerm: filters.searchTerm,
          types: typesStr,
          accessibilities: accessibilitiesStr,
          showVenueOptions: showVenueOptionsValue,
          openingHoursDay:
            filters.openingHours.mode === "custom"
              ? filters.openingHours.day
              : "",
          openingHoursStartTime:
            filters.openingHours.mode === "custom"
              ? filters.openingHours.time
              : "",
          openingHoursEndTime: "",
          // Add geocoding parameters
          latitude: geocodedLat?.toString() || "",
          longitude: geocodedLng?.toString() || "",
          address: geocodedAddress || "",
          // Add search context parameters
          search_type: undefined, // No specific type for manual keyword searches
          search_value: undefined,
          tagging_mode: tagging_mode || "off",
          placesToReplaceString: geocodedAddress || "",
        });
      }
    }

    setSelectedVenue(null);
    // A typed keyword can be a venue/brand (e.g. "EL&N") whose geocode resolves
    // to a broad, meaningless area — dropping a location pin there is confusing.
    // Only explicit Locations suggestions (applyAreaSearch) drop an anchor, so
    // here just clear any stale one and let the map frame the result pins.
    setLocationSearchAnchor(null);
    // Unlike handleSuggestionSelect, typed Enter/search never cleared the focus.
    setSearchPageParams((prev) => ({
      ...prev,
      focus_place_ids: "",
      focus_search: undefined,
    }));

    // Frame the map to the result pins once they load (works even when
    // VITE_ENABLE_AUTO_ZOOM is off), so a typed keyword search covers all venues.
    setFitMapToResults(Date.now());

    // If onPerformSearch is provided (search route), call it directly
    if (onPerformSearch && filters.searchTerm) {
      // Update mapCenter atom before calling performSearch
      // This prevents BasicMapView from using stale mapCenter and triggering unwanted updates
      if (geocodedLat && geocodedLng) {
        setMapCenter({
          lat: geocodedLat,
          lng: geocodedLng,
        });
        console.log(
          "SearchBar: Updated mapCenter for search on search page:",
          geocodedLat,
          geocodedLng,
        );
      }

      // Call performSearch directly with geocoded coordinates
      onPerformSearch(
        filters.searchTerm,
        null, // No search_type for keyword searches
        null, // No search_value for keyword searches
        geocodedLat || null,
        geocodedLng || null,
        geocodedAddress || null,
      );
    } else {
      // Determine which coordinates to use for search center (latitude/longitude)
      // Calculate these BEFORE setSearchPageParams so they're available for mapCenter update
      let finalLatitude: string | undefined;
      let finalLongitude: string | undefined;
      let finalLocationText: string | undefined;

      // Priority order:
      // 1. Use geocoded coordinates if we just geocoded a search term (HIGHEST PRIORITY - user intent in search)
      // 2. Use existing searchPageParams if they exist (previous search/selection location)
      // 3. Otherwise, copy location from home page (locationLatitude/locationLongitude)
      // Note: mapCenter is already being used for geocoding bias via getSafeCoordinates()

      if (geocodedLat && geocodedLng) {
        // ALWAYS use geocoded coordinates when available - they represent user intent
        finalLatitude = geocodedLat.toString();
        finalLongitude = geocodedLng.toString();
        finalLocationText = geocodedAddress;
      } else if (
        searchPageParams.latitude !== null &&
        searchPageParams.longitude !== null
      ) {
        // Use existing location from previous search/selection
        finalLatitude = searchPageParams.latitude;
        finalLongitude = searchPageParams.longitude;
        finalLocationText =
          searchPageParams.placesToReplaceString || currentLocationText;
      } else if (locationLatitude && locationLongitude) {
        // Fallback to home page location
        finalLatitude = locationLatitude.toString();
        finalLongitude = locationLongitude.toString();
        finalLocationText = currentLocationText;
      }

      // Otherwise, update params and navigate (index route)
      setSearchPageParams((prev) => {
        console.log("inside setSearchPageParams prev 2 = ", prev);

        // Carry the active map mode as-is. The toggle treats an unset map_mode
        // as "Places with data" (sociability), but mapping_status="mapped" is
        // only written on explicit toggle — so derive it here, otherwise a
        // typed search returns grey (unmapped) pins despite the toggle being
        // on. Declutter also needs mapping_status="mapped"; only "all" doesn't.
        const currentMapMode = prev.map_mode ?? "sociability";

        const searchParams = {
          ...prev,
          search: filters.searchTerm || "",
          types: typesStr || "",
          accessibilities: accessibilitiesStr || "",
          accessibility_sections: accessibilitySectionsStr || "",
          // Use determined coordinates if available
          ...(finalLatitude && { latitude: finalLatitude }),
          ...(finalLongitude && { longitude: finalLongitude }),
          ...(finalLocationText && {
            placesToReplaceString: finalLocationText,
          }),
          // Clear the stale viewport bbox so this new location isn't filtered by the old area.
          map_ne_lat: undefined,
          map_ne_lng: undefined,
          map_sw_lat: undefined,
          map_sw_lng: undefined,
          // Opening hours filters
          openingHoursMode: filters.openingHours.mode,
          openingHoursDay:
            filters.openingHours.mode === "custom"
              ? filters.openingHours.day
              : undefined,
          openingHoursTime:
            filters.openingHours.mode === "custom"
              ? filters.openingHours.time
              : undefined,
          showVenueOptions: getShowVenueOptions(filters.openingHours.mode),
          // Map mode is a POI-click feature; when the flag is off we send no
          // map_mode/mapping_status so results match the pre-feature behaviour.
          ...(enableGooglePoiClick
            ? {
                map_mode: currentMapMode,
                mapping_status: currentMapMode === "all" ? undefined : "mapped",
              }
            : {}),
          // Enable auto-zoom for dynamic fitBounds behavior
          autoZoom: "yes",
          // Trigger a refresh
          unixTimestamp: Math.floor(Date.now() / 1000),
        };

        // Clear search_type and search_value for keyword searches (only set when suggestion is selected)
        if (prev.search_type) delete searchParams.search_type;
        if (prev.search_value) delete searchParams.search_value;

        // Only set search_type for nearby search (when no search term and no filters)
        if (!filters.searchTerm && !typesStr) {
          searchParams.search_type = "";
          searchParams.search_value = "";
        }

        // Canonical search_method for place_search_results_viewed (resets any stale value, e.g. a
        // prior recent_search): a bare current-location search is "nearby", otherwise "typed_input".
        searchParams.search_method =
          !filters.searchTerm && !typesStr ? "nearby" : "typed_input";

        console.log("inside setSearchPageParams searchParams = ", searchParams);

        return searchParams;
      });

      // Update mapCenter atom to match the location we're navigating with
      // This prevents BasicMapView from using stale mapCenter and triggering unwanted updates
      if (finalLatitude && finalLongitude) {
        setMapCenter({
          lat: parseFloat(finalLatitude),
          lng: parseFloat(finalLongitude),
        });
        console.log(
          "SearchBar: Updated mapCenter for navigation:",
          finalLatitude,
          finalLongitude,
        );
      }

      // Navigate to search page
      navigate({ to: "/search" });
    }

    // Also call external callbacks if provided
    onSearchSubmit?.();
    onSearchWithFilters?.(filters);

    // Close overlay if in overlay mode (after all state updates)
    if (layout === "overlay" || layout === "overlaymobile") {
      onOverlayClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    closeAllPanels,
    layout,
    onOverlayClose,
    searchTerm,
    currentSelectedBusinessTypes,
    currentAccessibilityOptions,
    currentOpeningHoursMode,
    currentSelectedDay,
    currentSelectedTime,
    onSearchSubmit,
    onSearchWithFilters,
    onPerformSearch,
    setSearchPageParams,
    setMapCenter,
    navigate,
    getSafeCoordinates,
    isLoggedIn,
    addRecentSearch,
    tagging_mode,
    locationLatitude,
    locationLongitude,
    currentLocationText,
    setSelectedVenue,
    setLocationSearchAnchor,
    setFitMapToResults,
    i18n,
    mapCenter.lat,
    mapCenter.lng,
    searchPageParams.latitude,
    searchPageParams.longitude,
    searchPageParams.placesToReplaceString,
    searchPageParams.focus_place_ids,
    searchPageParams.focus_search,
  ]);

  // Wraps an action that may wait before it navigates - @see `onBusyChange`.
  const runBusy = useCallback(
    async (action: () => Promise<unknown> | unknown) => {
      onBusyChange?.(true);
      try {
        await action();
      } finally {
        onBusyChange?.(false);
      }
    },
    [onBusyChange],
  );

  // Hands the submit to a button outside the bar - @see `submitRef`.
  useEffect(() => {
    if (!submitRef) return;
    submitRef.current = () => void runBusy(handleSearchSubmit);
    return () => {
      submitRef.current = null;
    };
  }, [submitRef, handleSearchSubmit, runBusy]);

  // Handle suggestion selection - for header/overlay modes, handles navigation
  const handleSuggestionSelect = useCallback(
    async (suggestion: Suggestion) => {
      // Extract plain text from suggestion (remove HTML tags and commas)
      const plainText = (suggestion.name || suggestion.text || "")
        .replace(/<[^>]+>/g, "")
        .replace(/,/g, "");

      // Update the internal search term
      setInternalSearchTerm(plainText);

      setSelectedVenue(null);
      // Nor an area, so its reference marker goes too.
      setLocationSearchAnchor(null);

      // None of these suggestions focus a venue, so drop the one held above the
      // results before the new search runs.
      setSearchPageParams((prev) => ({
        ...prev,
        focus_place_ids: "",
        focus_search: undefined,
      }));

      // Brand and business_type are pure filter searches (search_type + search_value);
      // their name must NOT go into the `search` text query, matching the home-page
      // brand click. plainText is still kept for display / analytics / recent search.
      const searchTermForQuery =
        suggestion.suggestion_type === "brand" ||
        suggestion.suggestion_type === "business_type"
          ? ""
          : plainText;

      // Place / area / compound are handled by handleUnifiedSelect; this only
      // handles "search" types (venue_name w/o place_id, brand, business_type, location).

      if (
        layout === "header" ||
        layout === "overlay" ||
        layout === "overlaymobile"
      ) {
        // If onPerformSearch is provided (search route), call it directly
        if (onPerformSearch) {
          // Extract search parameters based on suggestion type
          let searchType: string | null = null;
          let searchValue: string | null = null;
          let lat: number | null = null;
          let lng: number | null = null;
          let address: string | null = null;

          if (suggestion.suggestion_type === "venue_name") {
            searchType = "venue_name";
            searchValue = suggestion.id;
            lat = suggestion.latitude || null;
            lng = suggestion.longitude || null;
            address = suggestion.address || null;
          } else if (suggestion.suggestion_type === "brand") {
            searchType = "brand";
            searchValue = suggestion.brand;
            // Use CURRENT location (viewport/search location), not brand's suggested location
            // This allows the map to expand based on search_distance_used to show all brand locations
            lat = latitude !== undefined ? latitude : null;
            lng = longitude !== undefined ? longitude : null;
            // Use existing placesToReplaceString to avoid duplicate geocoding call
            address = searchPageParams.placesToReplaceString || null;
          } else if (suggestion.suggestion_type === "location") {
            searchType = "location";
            searchValue = suggestion.text;

            // No lat/lng/address for location - will be geocoded by performSearch
          } else if (suggestion.suggestion_type === "business_type") {
            searchType = "business_type";
            searchValue = suggestion.business_type;
            // Use CURRENT location (viewport/search location)
            // This searches for the business type in the user's current location
            lat = latitude !== undefined ? latitude : null;
            lng = longitude !== undefined ? longitude : null;
            // Use existing placesToReplaceString to avoid duplicate geocoding call
            address = searchPageParams.placesToReplaceString || null;
          }

          // Store recent search if user is logged in
          console.log(
            "[SearchBar] Auto-suggest selected - Checking if should save recent search:",
            {
              isLoggedIn,
              plainText,
              suggestionType: suggestion.suggestion_type,
            },
          );
          if (isLoggedIn && plainText) {
            console.log(
              "[SearchBar] Saving recent search for auto-suggest selection",
            );
            const typesStr = currentSelectedBusinessTypes.join("|");
            const accessibilitiesStr = encodeSelection(currentAccessibilityOptions).accessibilities;
            const showVenueOptionsValue =
              currentOpeningHoursMode === "open_now"
                ? i18n.get("openNow")
                : currentOpeningHoursMode === "custom"
                  ? i18n.get("custom")
                  : i18n.get("all");

            addRecentSearch({
              searchTerm: plainText,
              types: typesStr,
              accessibilities: accessibilitiesStr,
              showVenueOptions: showVenueOptionsValue,
              openingHoursDay:
                currentOpeningHoursMode === "custom" ? currentSelectedDay : "",
              openingHoursStartTime:
                currentOpeningHoursMode === "custom" ? currentSelectedTime : "",
              openingHoursEndTime: "",
              // Add geocoding parameters
              latitude: lat?.toString() || "",
              longitude: lng?.toString() || "",
              address: address || "",
              // Add search context parameters
              search_type: searchType || undefined,
              search_value: searchValue || undefined,
              tagging_mode: tagging_mode || "off",
              placesToReplaceString: address || "",
            });
          }

          // Update mapCenter atom before calling performSearch
          // This prevents BasicMapView from using stale mapCenter and triggering unwanted updates
          if (lat !== null && lng !== null) {
            setMapCenter({
              lat: lat,
              lng: lng,
            });
            console.log(
              "SearchBar: Updated mapCenter for suggestion on search page:",
              lat,
              lng,
            );
          }

          // Call performSearch directly
          onPerformSearch(
            searchTermForQuery,
            searchType,
            searchValue,
            lat,
            lng,
            address,
            suggestionSearchMethod(suggestion.suggestion_type),
          );
        } else {
          // Otherwise, set params and navigate (index route)

          // Prepare variables for geocoded location and business_type
          let geocodedLat: number | undefined;
          let geocodedLng: number | undefined;
          let geocodedAddress: string | undefined;

          setSearchPageParams((prev) => {
            const searchParams = {
              ...prev,
              search: searchTermForQuery,
              // Trigger a refresh
              unixTimestamp: Math.floor(Date.now() / 1000),
              // Clear previous search parameters
              venue_id: undefined,
              search_type: undefined as string | undefined,
              search_value: undefined as string | undefined,
              // search_method = the entry PATH (canonical vocab) for this suggestion kind. location ->
              // neighborhood is preserved through performSearch; the rest derive to the same value.
              search_method: suggestionSearchMethod(
                suggestion.suggestion_type,
              ) as string | undefined,
            };

            // Add search parameters based on suggestion type
            if (suggestion.suggestion_type === "venue_name") {
              searchParams.search_type = "venue_name";
              searchParams.search_value = suggestion.id;
              searchParams.autoZoom = "yes";
              if (suggestion.latitude)
                searchParams.latitude = suggestion.latitude.toString();
              if (suggestion.longitude)
                searchParams.longitude = suggestion.longitude.toString();
              if (suggestion.address)
                searchParams.placesToReplaceString = suggestion.address;

              // Update mapCenter atom for venue_name
              if (suggestion.latitude && suggestion.longitude) {
                setMapCenter({
                  lat: suggestion.latitude,
                  lng: suggestion.longitude,
                });
                console.log(
                  "SearchBar: Updated mapCenter for venue suggestion:",
                  suggestion.latitude,
                  suggestion.longitude,
                );
              }
            } else if (suggestion.suggestion_type === "brand") {
              searchParams.search_type = "brand";
              searchParams.search_value = suggestion.brand;
              searchParams.autoZoom = "yes";
              // For brands, explicitly use CURRENT location (don't use suggestion.latitude/longitude)
              // This allows the map to expand based on search_distance_used to show all brand locations
              // Same behavior as home page brand click
              searchParams.latitude =
                latitude !== undefined ? latitude.toString() : undefined;
              searchParams.longitude =
                longitude !== undefined ? longitude.toString() : undefined;
              // Keep current placesToReplaceString without overwriting it
              // (simpleLocationText is already preserved in prev state)

              // Update mapCenter atom to match current location before navigating
              if (latitude !== undefined && longitude !== undefined) {
                setMapCenter({
                  lat: latitude,
                  lng: longitude,
                });
                console.log(
                  "SearchBar: Updated mapCenter for brand from home page:",
                  latitude,
                  longitude,
                );
              }
            } else if (suggestion.suggestion_type === "location") {
              searchParams.search_type = "location";
              searchParams.search_value = suggestion.text;
              searchParams.autoZoom = "yes";
              // Clear lat/lng/address for location - will be geocoded on search page
              searchParams.latitude = undefined;
              searchParams.longitude = undefined;
              searchParams.placesToReplaceString = undefined;
            } else if (suggestion.suggestion_type === "business_type") {
              searchParams.search_type = "business_type";
              searchParams.search_value = suggestion.business_type;
              searchParams.autoZoom = "yes";
              // Pass current location to search page
              searchParams.latitude =
                latitude !== undefined ? latitude.toString() : undefined;
              searchParams.longitude =
                longitude !== undefined ? longitude.toString() : undefined;
              // Keep current placesToReplaceString without overwriting it
              // (simpleLocationText is already preserved in prev state)

              // Update mapCenter atom to match current location before navigating
              if (latitude !== undefined && longitude !== undefined) {
                setMapCenter({
                  lat: latitude,
                  lng: longitude,
                });
                console.log(
                  "SearchBar: Updated mapCenter for business_type from home page:",
                  latitude,
                  longitude,
                );
              }
            }

            return searchParams;
          });

          // Store recent search if user is logged in
          console.log(
            "[SearchBar] Auto-suggest (else branch) - Checking if should save recent search:",
            {
              isLoggedIn,
              plainText,
              suggestionType: suggestion.suggestion_type,
            },
          );
          if (isLoggedIn && plainText) {
            console.log(
              "[SearchBar] Saving recent search for auto-suggest selection (else branch)",
            );
            const typesStr = currentSelectedBusinessTypes.join("|");
            const accessibilitiesStr = encodeSelection(currentAccessibilityOptions).accessibilities;
            const showVenueOptionsValue =
              currentOpeningHoursMode === "open_now"
                ? i18n.get("openNow")
                : currentOpeningHoursMode === "custom"
                  ? i18n.get("custom")
                  : i18n.get("all");

            let lat: string = "";
            let lng: string = "";
            let address: string = "";
            let searchType: string | undefined;
            let searchValue: string | undefined;

            if (suggestion.suggestion_type === "venue_name") {
              searchType = "venue_name";
              searchValue = suggestion.id;
              lat = suggestion.latitude?.toString() || "";
              lng = suggestion.longitude?.toString() || "";
              address = suggestion.address || "";
            } else if (suggestion.suggestion_type === "brand") {
              searchType = "brand";
              searchValue = suggestion.brand;
              // Use current location for brand search, not suggestion coordinates
              lat = latitude?.toString() || "";
              lng = longitude?.toString() || "";
              address = locationText || "";
            } else if (suggestion.suggestion_type === "location") {
              searchType = "location";
              searchValue = suggestion.text;
              lat = geocodedLat?.toString() || "";
              lng = geocodedLng?.toString() || "";
              address = geocodedAddress || "";
            } else if (suggestion.suggestion_type === "business_type") {
              searchType = "business_type";
              searchValue = suggestion.business_type;
              // Use current location for business_type search
              lat = latitude?.toString() || "";
              lng = longitude?.toString() || "";
              address = locationText || "";
            }

            console.log("[SearchBar] Calling addRecentSearch with data:", {
              searchTerm: plainText,
              types: typesStr,
              searchType,
              searchValue,
              lat,
              lng,
              address,
            });

            addRecentSearch({
              searchTerm: plainText,
              types: typesStr,
              accessibilities: accessibilitiesStr,
              showVenueOptions: showVenueOptionsValue,
              openingHoursDay:
                currentOpeningHoursMode === "custom" ? currentSelectedDay : "",
              openingHoursStartTime:
                currentOpeningHoursMode === "custom" ? currentSelectedTime : "",
              openingHoursEndTime: "",
              // Add geocoding parameters
              latitude: lat,
              longitude: lng,
              address: address,
              // Add search context parameters
              search_type: searchType,
              search_value: searchValue,
              tagging_mode: tagging_mode || "off",
              placesToReplaceString: address,
            });
          }

          // Navigate to search page
          navigate({ to: "/search" });
        }
      }

      // suggestion_item_card -> place_search_performed. brand_id/business_id only for the matching
      // suggestion type; otherwise empty.
      const suggestionSummary = buildFilterSummary();
      trackEvent("place_search_performed", {
        // Brands are name-based and can come from Google, so there is no id.
        brand_id: "",
        brand_name:
          suggestion.suggestion_type === "brand"
            ? (suggestion.brand ?? "")
            : "",
        business_id:
          suggestion.suggestion_type === "venue_name"
            ? (suggestion.id ?? "")
            : "",
        place_name:
          suggestion.suggestion_type === "venue_name"
            ? (suggestion.name ?? plainText)
            : "",
        filters_count: suggestionSummary.filters_count,
        search_method: suggestionSearchMethod(suggestion.suggestion_type),
        search_query: plainText,
        // place / brand / neighborhood per the clicked suggestion; default place.
        // A neighbourhood autosuggest item is typed "location" (not "neighborhood") —
        // matching this is what keeps search_result_type aligned with the results page.
        search_result_type:
          suggestion.suggestion_type === "brand"
            ? "brand"
            : suggestion.suggestion_type === "location" ||
                suggestion.suggestion_type === "neighborhood"
              ? "neighborhood"
              : "place",
        selected_filter: suggestionSummary.selected_filter,
      });

      // Also call external callback if provided
      onSelectSuggestion?.(suggestion);

      // Close overlay if in overlay mode (after all state updates)
      if (layout === "overlay" || layout === "overlaymobile") {
        onOverlayClose?.();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      layout,
      onOverlayClose,
      closeAllPanels,
      setSearchPageParams,
      setMapCenter,
      navigate,
      onSelectSuggestion,
      onPerformSearch,
      isLoggedIn,
      addRecentSearch,
      currentSelectedBusinessTypes,
      currentAccessibilityOptions,
      currentOpeningHoursMode,
      currentSelectedDay,
      currentSelectedTime,
      tagging_mode,
      setInternalSearchTerm,
      i18n,
      latitude,
      longitude,
      locationText,
      setSelectedVenue,
      setLocationSearchAnchor,
      searchPageParams.placesToReplaceString,
      setPendingGooglePlaceId,
    ],
  );

  // Pan to (lat,lng) and run a "search this area". Shared by PAN_AND_NEARBY + CATEGORY_IN_AREA.
  const applyAreaSearch = useCallback(
    (
      lat: number,
      lng: number,
      label: string,
      extra?: {
        search?: string;
        search_type?: string;
        search_value?: string;
      },
    ) => {
      setMapCenter({ lat, lng });
      // Unconditional pan (like performSearch) so every area pick moves the map.
      window.dispatchEvent(
        new CustomEvent("panMapToCenter", { detail: { lat, lng } }),
      );
      // No venue pin exists at this point (it's an area/landmark/station, not a
      // business) — drop a reference marker so the map view has a visible anchor.
      setLocationSearchAnchor({ lat, lng, label });

      // Ask the map to fit to the result pins once they load. This works even
      // when VITE_ENABLE_AUTO_ZOOM is off, since an explicit area/category
      // search should always frame its results.
      setFitMapToResults(Date.now());

      // Area/neighbourhood picks never logged a recent search — same gap as venue picks below.
      if (isLoggedIn) {
        const typesStr = currentSelectedBusinessTypes.join("|");
        const accessibilitiesStr = encodeSelection(currentAccessibilityOptions).accessibilities;
        const showVenueOptionsValue =
          currentOpeningHoursMode === "open_now"
            ? i18n.get("openNow")
            : currentOpeningHoursMode === "custom"
              ? i18n.get("custom")
              : i18n.get("all");

        addRecentSearch({
          searchTerm: extra?.search || label,
          types: typesStr,
          accessibilities: accessibilitiesStr,
          showVenueOptions: showVenueOptionsValue,
          openingHoursDay:
            currentOpeningHoursMode === "custom" ? currentSelectedDay : "",
          openingHoursStartTime:
            currentOpeningHoursMode === "custom" ? currentSelectedTime : "",
          openingHoursEndTime: "",
          latitude: lat.toString(),
          longitude: lng.toString(),
          address: label,
          search_type: extra?.search_type ?? "location",
          search_value: extra?.search_value,
          tagging_mode: tagging_mode || "off",
          placesToReplaceString: label,
        });
      }

      // Preserve the active map mode as-is (unset defaults to sociability).
      const currentMapMode = searchPageParams.map_mode ?? "sociability";

      const base = {
        latitude: lat.toString(),
        longitude: lng.toString(),
        search: extra?.search ?? "",
        search_type: extra?.search_type as string | undefined,
        search_value: extra?.search_value as string | undefined,
        // An area search replaces any venue held above the results.
        focus_place_ids: "",
        focus_search: undefined as string | undefined,
        // An area/neighbourhood pick — carry the method so place_search_results_viewed
        // reports search_method:neighborhood + search_result_type:neighborhood instead
        // of falling back to typed_input/place (this path fires no place_search_performed).
        search_method: "neighborhood",
        searchThisAreaClicked: "no",
        autoZoom: "yes",
        // Clear the stale viewport bbox so this new location isn't filtered by the old area.
        map_ne_lat: undefined,
        map_ne_lng: undefined,
        map_sw_lat: undefined,
        map_sw_lng: undefined,
        // Map mode is a POI-click feature; skip when the flag is off.
        ...(enableGooglePoiClick
          ? {
              map_mode: currentMapMode,
              mapping_status: currentMapMode === "all" ? undefined : "mapped",
            }
          : {}),
        unixTimestamp: Math.floor(Date.now() / 1000),
      };

      // No onPerformSearch means there's no map under us (home page): coords are
      // resolved here and BasicMapView centres + searches on mount. Covers the
      // docked bar and both of the home page's overlays, same as a venue pick.
      if (
        (layout === "header" ||
          layout === "overlay" ||
          layout === "overlaymobile") &&
        !onPerformSearch
      ) {
        setSearchPageParams((prev) => ({ ...prev, ...base }));
        navigate({ to: "/search" });
        closeAllPanels();
        if (layout === "overlay" || layout === "overlaymobile") {
          onOverlayClose?.();
          setInternalSearchTerm(label);
        }
        (document.activeElement as HTMLElement | null)?.blur?.();
        return;
      }

      setSearchPageParams((prev) => ({ ...prev, ...base }));
      closeAllPanels();
      if (layout === "overlay" || layout === "overlaymobile") {
        onOverlayClose?.();
        setInternalSearchTerm(label);
      }
      (document.activeElement as HTMLElement | null)?.blur?.();
    },
    [
      layout,
      onPerformSearch,
      setMapCenter,
      setSearchPageParams,
      navigate,
      closeAllPanels,
      onOverlayClose,
      setInternalSearchTerm,
      searchPageParams.map_mode,
      setFitMapToResults,
      setLocationSearchAnchor,
      isLoggedIn,
      addRecentSearch,
      currentSelectedBusinessTypes,
      currentAccessibilityOptions,
      currentOpeningHoursMode,
      currentSelectedDay,
      currentSelectedTime,
      i18n,
      tagging_mode,
    ],
  );

  // Action dispatcher: the backend decided what each suggestion does (item.action).
  const handleUnifiedSelect = useCallback(
    async (item: UnifiedSuggestItem, sessionToken: string) => {
      const { action, data, displayText } = item;
      setInternalSearchTerm(displayText);
      // Areas resolve coordinates over the network; a late one must not drop its
      // marker over the venue picked since.
      const selection = ++selectionSeqRef.current;
      const isStale = () => selection !== selectionSeqRef.current;

      switch (action.type) {
        case "OPEN_VENUE":
        case "CREATE_GREY_PIN": {
          const placeId = action.placeId ?? data.place_id ?? null;

          // No place_id → can't use the map/POI flow; fall back to venue_name search.
          // Also fall back when the POI-click feature is disabled: BasicMapView's
          // `selectGooglePlace` handler ignores events while the flag is off, so the
          // map/POI flow would be a no-op. For in-DB venues this opens the venue via
          // its businessId; for grey pins it degrades to a text search by name.
          if (!placeId || !enableGooglePoiClick) {
            await handleSuggestionSelect({
              id: data.businessId != null ? String(data.businessId) : null,
              slug: data.slug ?? null,
              place_id: null,
              name: displayText,
              address: null,
              city: null,
              business_type: data.businessType ?? null,
              latitude: data.latitude ?? null,
              longitude: data.longitude ?? null,
              completeness: null,
              text: displayText,
              distanceKm: null,
              distanceText: null,
              suggestion_type: "venue_name",
            });
            return;
          }

          // Grey pins have no business_id yet — the venue record is created after this point.
          trackSuggestionSearch({
            business_id: data.businessId != null ? String(data.businessId) : "",
            place_name: displayText,
            search_query: displayText,
            search_method: "typed_input",
            search_result_type: "place",
          });

          setSelectedVenue(null);
          setLocationSearchAnchor(null);

          // Picking a resolved venue skipped addRecentSearch entirely — log it here too.
          if (isLoggedIn) {
            const typesStr = currentSelectedBusinessTypes.join("|");
            const accessibilitiesStr = encodeSelection(currentAccessibilityOptions).accessibilities;
            const showVenueOptionsValue =
              currentOpeningHoursMode === "open_now"
                ? i18n.get("openNow")
                : currentOpeningHoursMode === "custom"
                  ? i18n.get("custom")
                  : i18n.get("all");

            addRecentSearch({
              searchTerm: displayText,
              types: typesStr,
              accessibilities: accessibilitiesStr,
              showVenueOptions: showVenueOptionsValue,
              openingHoursDay:
                currentOpeningHoursMode === "custom" ? currentSelectedDay : "",
              openingHoursStartTime:
                currentOpeningHoursMode === "custom" ? currentSelectedTime : "",
              openingHoursEndTime: "",
              latitude: data.latitude?.toString() || "",
              longitude: data.longitude?.toString() || "",
              address: "",
              search_type: "venue_name",
              search_value:
                data.businessId != null ? String(data.businessId) : placeId,
              tagging_mode: tagging_mode || "off",
              placesToReplaceString: "",
            });
          }

          // In-DB venue needs no billable Place Details (we have its data), so close
          // the session with a free call. Grey pins close via place-from-google's
          // Details call, so keep forwarding the token there.
          const inDb = action.type === "OPEN_VENUE";
          if (inDb) closeSuggestSession(placeId, sessionToken);
          const followUpToken = inDb ? "" : sessionToken;

          // Home page (no map): stash place_id + navigate; BasicMapView resolves it
          // on mount and shows the preview card. Leave `search` empty so we get a
          // nearby search behind the card, not a text search for the venue name.
          // Covers the docked home header bar (layout "header") and any overlay
          // opened from a route with no map underneath it — the home page's own
          // "overlay" (narrow desktop) and "overlaymobile" (phone width) both omit
          // onPerformSearch; the /search page's versions always pass one.
          if (
            (layout === "header" ||
              layout === "overlay" ||
              layout === "overlaymobile") &&
            !onPerformSearch
          ) {
            setPendingGooglePlaceId(placeId);
            const navLat = data.latitude || latitude;
            const navLng = data.longitude || longitude;
            setSearchPageParams((prev) => ({
              ...prev,
              search: "",
              search_type: undefined,
              search_value: undefined,
              focus_place_ids: placeId,
              // The term a later re-search will write, so it isn't read as new.
              focus_search: displayText,
              unixTimestamp: Math.floor(Date.now() / 1000),
              ...(navLat &&
                navLng &&
                !isNaN(navLat) &&
                !isNaN(navLng) && {
                  latitude: navLat.toString(),
                  longitude: navLng.toString(),
                }),
            }));
            navigate({ to: "/search" });
            closeAllPanels();
            if (layout === "overlay" || layout === "overlaymobile") {
              onOverlayClose?.();
            }
            (document.activeElement as HTMLElement | null)?.blur?.();
            return;
          }

          // The list reload below spreads prev, so the focus survives it. Drop the
          // previous query so a leftover brand/category search doesn't read as a
          // new one and cancel the focus.
          setSearchPageParams((prev) => ({
            ...prev,
            search: "",
            search_type: undefined,
            search_value: undefined,
            focus_place_ids: placeId,
            focus_search: displayText,
          }));

          // Resolve + show the preview card; BasicMapView also reloads the list
          // nearby the venue using its resolved coordinates.
          window.dispatchEvent(
            new CustomEvent("selectGooglePlace", {
              detail: {
                placeId,
                sessionToken: followUpToken,
                name: displayText,
                // Unique per action so re-selecting the same place isn't dropped by the dedupe.
                requestId: `${placeId}:${Date.now()}:${Math.random()
                  .toString(36)
                  .slice(2)}`,
              },
            }),
          );
          closeAllPanels();
          if (layout === "overlay" || layout === "overlaymobile") {
            onOverlayClose?.();
            setInternalSearchTerm(displayText);
          }
          (document.activeElement as HTMLElement | null)?.blur?.();
          return;
        }

        case "PAN_AND_NEARBY": {
          setSelectedVenue(null);
          const res = await resolveSuggestionArea({
            placeId: action.placeId,
            sessionToken,
          });
          if (!res || isStale()) return;
          trackSuggestionSearch({
            search_query: displayText,
            search_method: "neighborhood",
            search_result_type: "neighborhood",
          });
          applyAreaSearch(res.lat, res.lng, displayText);
          return;
        }

        case "CATEGORY_IN_AREA": {
          setSelectedVenue(null);
          // Prefer the unambiguous place_id; bias the text fallback to the map centre.
          const areaPlaceId = data.place_id ?? null;
          const res = await resolveSuggestionArea({
            placeId: areaPlaceId,
            query: areaPlaceId ? null : action.locationQuery,
            sessionToken,
            latitude,
            longitude,
          });
          if (!res || isStale()) return;
          // search_method mirrors what applyAreaSearch writes to searchPageParams, so this
          // stays aligned with the place_search_results_viewed that follows.
          trackSuggestionSearch({
            search_query: displayText,
            search_method: "neighborhood",
            search_result_type: "neighborhood",
          });
          // Filter by matched business type, else fall back to a text search.
          applyAreaSearch(res.lat, res.lng, displayText, {
            search: action.businessType ? "" : action.fallbackText,
            search_type: action.businessType ? "business_type" : undefined,
            search_value: action.businessType
              ? action.businessType.toLowerCase()
              : undefined,
          });
          return;
        }

        case "BUSINESS_TYPE": {
          await handleSuggestionSelect({
            id: null,
            slug: null,
            place_id: null,
            name: displayText,
            address: null,
            city: null,
            business_type: action.businessType ?? null,
            latitude: null,
            longitude: null,
            completeness: null,
            text: displayText,
            distanceKm: null,
            distanceText: null,
            suggestion_type: "business_type",
          });
          return;
        }

        case "BRAND": {
          await handleSuggestionSelect({
            id: null,
            slug: null,
            place_id: null,
            name: displayText,
            address: null,
            city: null,
            business_type: null,
            brand: action.brand ?? null,
            latitude: null,
            longitude: null,
            completeness: null,
            text: displayText,
            distanceKm: null,
            distanceText: null,
            suggestion_type: "brand",
          });
          return;
        }

        case "TEXT_SEARCH": {
          await handleSuggestionSelect({
            id: null,
            slug: null,
            place_id: null,
            name: displayText,
            address: null,
            city: null,
            business_type: null,
            latitude: data.latitude ?? null,
            longitude: data.longitude ?? null,
            completeness: null,
            text: action.searchValue ?? action.text ?? displayText,
            distanceKm: null,
            distanceText: null,
            suggestion_type:
              action.searchType === "location" ? "location" : "venue_name",
          });
          return;
        }
      }
    },
    [
      handleSuggestionSelect,
      applyAreaSearch,
      setInternalSearchTerm,
      setSelectedVenue,
      layout,
      onPerformSearch,
      onOverlayClose,
      setPendingGooglePlaceId,
      setSearchPageParams,
      navigate,
      closeAllPanels,
      latitude,
      longitude,
      isLoggedIn,
      addRecentSearch,
      currentSelectedBusinessTypes,
      currentAccessibilityOptions,
      currentOpeningHoursMode,
      currentSelectedDay,
      currentSelectedTime,
      i18n,
      tagging_mode,
    ],
  );

  // Keyed on the pick's id, so the same suggestion picked twice still runs.
  const lastPendingIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!pendingSuggestion || lastPendingIdRef.current === pendingSuggestion.id)
      return;
    lastPendingIdRef.current = pendingSuggestion.id;
    void handleUnifiedSelect(
      pendingSuggestion.item,
      pendingSuggestion.sessionToken,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSuggestion]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      if (
        layout === "header" ||
        layout === "overlay" ||
        layout === "overlaymobile"
      ) {
        setInternalSearchTerm(value);
      }
      onSearchChange?.(value);

      // Show autosuggest when there's text
      if (value.trim().length > 0) {
        setActivePanel("suggestions");
      }
    },
    [layout, onSearchChange, setInternalSearchTerm],
  );

  const handleClearSearch = () => {
    // Back to the default nearby search. The results list and the results-page
    // title both read these params, so leaving the old term behind kept the
    // previous venue/brand name in the header after the field was emptied.
    setSearchPageParams((prev) => ({
      ...prev,
      search: "",
      search_type: "nearby",
      search_value: i18n.get("nearBy"),
      search_method: undefined,
      simpleLocationText: "",
      focus_place_ids: "",
      focus_search: undefined,
      unixTimestamp: Math.floor(Date.now() / 1000),
    }));
    if (
      layout === "header" ||
      layout === "overlay" ||
      layout === "overlaymobile"
    ) {
      setInternalSearchTerm("");
    }
    onSearchChange?.("");
    if (onClearSearch) {
      onClearSearch();
    }
  };

  // Handle saved searches click for header mode
  const handleSavedSearchesClick = useCallback(() => {
    if (
      layout === "header" ||
      layout === "overlay" ||
      layout === "overlaymobile"
    ) {
      setInternalSavedSearchesOpen(!internalSavedSearchesOpen);
      // Close any active panels when toggling saved searches
      if (!internalSavedSearchesOpen) {
        closeAllPanels();
      }
    }
    onSavedSearchesClick?.();
  }, [layout, internalSavedSearchesOpen, onSavedSearchesClick, closeAllPanels]);

  // Determine if saved searches is open
  const isSavedSearchesOpenState =
    layout === "header" || layout === "overlay" || layout === "overlaymobile"
      ? internalSavedSearchesOpen
      : isSavedSearchesOpen;

  // Transform saved searches to combobox options
  const savedSearchOptions: ComboboxOption[] = (savedSearches || []).map(
    (search) => ({
      value: String(search.id),
      label: search.name,
    }),
  );

  // Handle saved search selection
  const handleSavedSearchSelect = useCallback(
    (search: SavedSearch) => {
      // saved_search_item_card -> place_search_performed. Props from the saved search's own filters
      // (selected_filter = business-type names + resolved accessibility names).
      const savedAccNameMap = new Map<string, string>();
      (accessibilityTagsData ?? []).forEach((o) =>
        o?.accessibility_tags?.forEach((t) =>
          savedAccNameMap.set(String(t.id), t.name),
        ),
      );
      const savedBusinessTypes = Array.isArray(search.filters?.business_types)
        ? search.filters.business_types
        : search.filters?.business_types
          ? [search.filters.business_types]
          : [];
      const savedAccNames = (
        Array.isArray(search.filters?.accessibilities)
          ? search.filters.accessibilities
          : search.filters?.accessibilities
            ? [search.filters.accessibilities]
            : []
      )
        .map((id) => savedAccNameMap.get(String(id)))
        .filter((n): n is string => Boolean(n));
      const savedSelected = [...savedBusinessTypes, ...savedAccNames];
      trackEvent("place_search_performed", {
        brand_id: "",
        brand_name: "",
        business_id: "",
        place_name: "",
        filters_count:
          savedSelected.length +
          (search.filters?.open_now || search.filters?.opening_hours ? 1 : 0),
        search_method: "saved_search",
        search_query: search.search_term ?? "",
        search_result_type: "place",
        selected_filter: savedSelected,
      });

      // Close the dropdown
      if (
        layout === "header" ||
        layout === "overlay" ||
        layout === "overlaymobile"
      ) {
        setInternalSavedSearchesOpen(false);
      }

      // Call the parent callback if provided
      if (onSavedSearchSelect) {
        onSavedSearchSelect(search);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout, onSavedSearchSelect],
  );

  // Handle saved search selection from combobox
  const handleSavedSearchSelectFromCombobox = useCallback(
    (value: string) => {
      // Find the selected search by ID
      const selectedSearch = savedSearches?.find(
        (search) => String(search.id) === value,
      );
      if (!selectedSearch) return;

      // Close the dropdown
      if (
        layout === "header" ||
        layout === "overlay" ||
        layout === "overlaymobile"
      ) {
        setInternalSavedSearchesOpen(false);
      }

      // Call the parent callback if provided
      if (onSavedSearchSelect) {
        onSavedSearchSelect(selectedSearch);
      }
    },
    [layout, onSavedSearchSelect, savedSearches],
  );

  // Sync active segment with saved searches state
  useEffect(() => {
    if (layout === "overlaymobile") {
      setActiveSegment(isSavedSearchesOpenState ? "saved" : "new");
    }
  }, [isSavedSearchesOpenState, layout]);

  // Handle input focus - for button mode only
  const handleInputFocus = () => {
    onSearchFocus?.();
  };

  // Handle accessibility filter click
  const handleAccessibilityClick = () => {
    const wasOpen = accessibilityWasOpenRef.current;
    accessibilityWasOpenRef.current = false;

    if (wasOpen) {
      setActivePanel("none");
      fireFilterClosed("accessibility");
      onAccessibilityClick?.();
      return;
    }

    // Save reference to trigger button for focus return
    if (accessibilityButtonRef.current) {
      lastTriggerButtonRef.current = accessibilityButtonRef.current;
    }

    setActivePanel("accessibility");
    filterClosedFiredRef.current = false; // new open-session: allow the next dismiss to fire filter_closed
    trackEvent("filter_opened", {
      filter_type: "accessibility",
      ...buildFilterSummary(),
    });
    onAccessibilityClick?.();
  };

  // Handle business type click
  const handleBusinessTypeClick = () => {
    const wasOpen = businessTypeWasOpenRef.current;
    businessTypeWasOpenRef.current = false;

    if (wasOpen) {
      setActivePanel("none");
      fireFilterClosed("business_type");
      onBusinessTypeClick?.();
      return;
    }

    // Save reference to trigger button for focus return
    if (businessTypeButtonRef.current) {
      lastTriggerButtonRef.current = businessTypeButtonRef.current;
    }

    setActivePanel("businessType");
    filterClosedFiredRef.current = false; // new open-session: allow the next dismiss to fire filter_closed
    trackEvent("filter_opened", {
      filter_type: "business_type",
      ...buildFilterSummary(),
    });
    onBusinessTypeClick?.();
  };

  // Handle opening hours click
  const handleOpeningHourClick = () => {
    const wasOpen = openingHoursWasOpenRef.current;
    openingHoursWasOpenRef.current = false;

    if (wasOpen) {
      setActivePanel("none");
      fireFilterClosed("opening_hours");
      onOpeningHourClick?.();
      return;
    }

    // Save reference to trigger button for focus return
    if (openingHoursButtonRef.current) {
      lastTriggerButtonRef.current = openingHoursButtonRef.current;
    }

    setActivePanel("openingHours");
    filterClosedFiredRef.current = false; // new open-session: allow the next dismiss to fire filter_closed
    trackEvent("filter_opened", {
      filter_type: "opening_hours",
      ...buildFilterSummary(),
    });
    onOpeningHourClick?.();
  };

  // Handle accessibility options change
  const handleAccessibilityOptionsChange = (keys: SelectionKey[]) => {
    setInternalAccessibilityOptions(keys);
    onAccessibilityOptionsChange?.(keys);
  };

  // Handle accessibility clear all
  const handleAccessibilityClearAll = () => {
    // per-panel "Clear all" -> filter_cleared. filters_count = total active filters at clear time
    // (read before the reset below). The global Reset fires filter_cleared separately.
    trackEvent("filter_cleared", {
      filters_count: buildFilterSummary().filters_count,
    });
    setInternalAccessibilityOptions([]);
    onAccessibilityClearAll?.();
  };

  // handleSearchSubmit reads the selection from state, so the search waits for the cleared
  // selection to render instead of running in the click with the old one.
  const [submitAfterClear, setSubmitAfterClear] = useState(false);
  useEffect(() => {
    if (!submitAfterClear) return;
    setSubmitAfterClear(false);
    void handleSearchSubmit();
  }, [submitAfterClear, handleSearchSubmit]);

  const handleAccessibilityClearAndSubmit = () => {
    handleAccessibilityClearAll();
    setSubmitAfterClear(true);
  };

  // Handle opening hours mode change
  const handleOpeningHoursModeChange = (mode: OpeningHoursMode) => {
    setInternalOpeningHoursMode(mode);
    onOpeningHoursModeChange?.(mode);
  };

  // Handle day change
  const handleDayChange = (day: string) => {
    setInternalSelectedDay(day);
    onDayChange?.(day);
  };

  // Handle time change
  const handleTimeChange = (time: string) => {
    setInternalSelectedTime(time);
    onTimeChange?.(time);
  };

  // Handle business type selection change
  const handleBusinessTypesChange = (types: string[]) => {
    setInternalSelectedBusinessTypes(types);
    onBusinessTypesChange?.(types);
  };

  // Handle business type toggle
  const handleBusinessTypeToggle = (typeName: string) => {
    const isCurrentlySelected = currentSelectedBusinessTypes.includes(typeName);
    let newSelection: string[];

    if (isCurrentlySelected) {
      newSelection = currentSelectedBusinessTypes.filter((t) => t !== typeName);
    } else {
      newSelection = [...currentSelectedBusinessTypes, typeName];
    }

    // Update internal state
    setInternalSelectedBusinessTypes(newSelection);

    // Call external callbacks
    onBusinessTypeToggle?.(typeName);
    onBusinessTypesChange?.(newSelection);
  };

  // Handle nearby places click
  const handleNearbyPlacesClick = useCallback(() => {
    // Update home search params for nearby search
    setSearchPageParams((prev) => ({
      ...prev,
      search: "",
      search_type: "nearby",
      search_value: i18n.get("nearBy"),
      autoZoom: "yes",
      unixTimestamp: Math.floor(Date.now() / 1000),
    }));

    // Navigate to search page
    navigate({ to: "/search" });

    onNearbyPlacesClick?.();

    // Close overlay if in overlay mode (after all state updates)
    if (layout === "overlay" || layout === "overlaymobile") {
      onOverlayClose?.();
    }
  }, [
    layout,
    onOverlayClose,
    setSearchPageParams,
    navigate,
    onNearbyPlacesClick,
  ]);

  // Handle keyboard navigation for recent search pills
  const handleRecentSearchKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLButtonElement>,
      index: number,
      totalCount: number,
    ) => {
      let targetIndex: number | null = null;

      switch (e.key) {
        case "ArrowRight":
          e.preventDefault();
          targetIndex = index < totalCount - 1 ? index + 1 : index;
          break;
        case "ArrowLeft":
          e.preventDefault();
          targetIndex = index > 0 ? index - 1 : index;
          break;
        case "Home":
          e.preventDefault();
          targetIndex = 0;
          break;
        case "End":
          e.preventDefault();
          targetIndex = totalCount - 1;
          break;
        default:
          return;
      }

      if (targetIndex !== null && recentSearchRefs.current[targetIndex]) {
        recentSearchRefs.current[targetIndex]?.focus();
      }
    },
    [],
  );

  // Handle recent search selection
  const handleRecentSearchSelect = useCallback(
    async (search: RecentSearchResult) => {
      // recent_search_item_card -> place_search_performed (mirrors the saved_search / neighborhood
      // handlers). selected_filter = business-type names + resolved accessibility names from the
      // recent search's own filters; filters_count adds an opening-hours filter when present.
      // onPerformSearch (search.lazy.tsx performSearch) does NOT fire this, so no double-fire.
      const recentAccNameMap = new Map<string, string>();
      (accessibilityTagsData ?? []).forEach((o) =>
        o?.accessibility_tags?.forEach((t) =>
          recentAccNameMap.set(String(t.id), t.name),
        ),
      );
      const recentBusinessTypes = Array.isArray(search.filters?.business_types)
        ? search.filters.business_types
        : search.filters?.business_types
          ? [search.filters.business_types]
          : [];
      const recentAccNames = (
        Array.isArray(search.filters?.accessibilities)
          ? search.filters.accessibilities
          : search.filters?.accessibilities
            ? [search.filters.accessibilities]
            : []
      )
        .map((id) => recentAccNameMap.get(String(id)))
        .filter((n): n is string => Boolean(n));
      const recentSelected = [...recentBusinessTypes, ...recentAccNames];
      trackEvent("place_search_performed", {
        brand_id: "",
        brand_name: "",
        business_id: "",
        place_name: "",
        filters_count:
          recentSelected.length +
          (search.filters?.open_now || search.filters?.opening_hours ? 1 : 0),
        search_method: "recent_search",
        search_query: search.search_term ?? "",
        search_result_type: "place",
        selected_filter: recentSelected,
      });

      // Update internal search term
      setInternalSearchTerm(search.search_term);

      // Build filter strings from the recent search
      // Handle both string and array formats for business_types
      const typesStr = Array.isArray(search.filters?.business_types)
        ? search.filters.business_types.join("|")
        : search.filters?.business_types || "";

      // Handle both string and array formats for accessibilities
      const accessibilitiesStr = Array.isArray(search.filters?.accessibilities)
        ? search.filters.accessibilities.join("|")
        : search.filters?.accessibilities || "";

      // Map opening hours to showVenueOptions
      const getShowVenueOptions = (): string => {
        if (search.filters?.open_now) return i18n.get("openNow");
        if (search.filters?.opening_hours?.day?.length)
          return i18n.get("custom");
        return i18n.get("all");
      };

      setSelectedVenue(null);
      setLocationSearchAnchor(null);

      // A venue row stores the venue's id in search_value and carries no
      // coordinates of its own, so the geocode fallback below would resolve its
      // NAME as if it were a place — turning the replay into a free-text search
      // somewhere unrelated. Skip it and let search_type/search_value narrow to
      // the venue instead, which is the same path autosuggest takes for a venue
      // with no place_id.
      const isVenueRecentSearch =
        search.filters?.search_type === "venue_name" &&
        Boolean(search.filters?.search_value);

      // If onPerformSearch is provided (search route), call it directly
      if (onPerformSearch && search.search_term) {
        let geocodedLat: number | undefined;
        let geocodedLng: number | undefined;
        let geocodedAddress: string | undefined;

        // Use stored coordinates if available, otherwise geocode
        if (search.filters?.latitude && search.filters?.longitude) {
          // Use stored coordinates from recent search
          geocodedLat = parseFloat(search.filters.latitude);
          geocodedLng = parseFloat(search.filters.longitude);
          geocodedAddress =
            search.filters.address || search.filters.placesToReplaceString;
        } else if (search.search_term && !isVenueRecentSearch) {
          // Geocode only if coordinates not stored (backwards compatibility)
          try {
            const { lat: safeLat, lng: safeLng } = getSafeCoordinates();
            const { lat, lng, address, error } =
              await getFirstResultLocationAndAddress(
                search.search_term,
                safeLat,
                safeLng,
              );

            if (!error) {
              geocodedLat = lat;
              geocodedLng = lng;
              geocodedAddress = address;
            }
          } catch (error) {
            console.error("Error geocoding search term:", error);
          }
        }

        // A venue row carries no coordinates, so centre on where the user
        // already is and let search_type/search_value narrow to the venue.
        // performSearch only skips its own geocode when lat, lng AND address
        // are all non-null, so all three have to be supplied here.
        if (isVenueRecentSearch && geocodedLat === undefined) {
          const { lat: safeLat, lng: safeLng } = getSafeCoordinates();
          geocodedLat = safeLat;
          geocodedLng = safeLng;
          geocodedAddress = locationText || "";
        }

        // Update mapCenter atom before calling performSearch
        // This prevents BasicMapView from using stale mapCenter and triggering unwanted updates
        if (geocodedLat !== undefined && geocodedLng !== undefined) {
          setMapCenter({
            lat: geocodedLat,
            lng: geocodedLng,
          });
          console.log(
            "SearchBar: Updated mapCenter for recent search on search page:",
            geocodedLat,
            geocodedLng,
          );
        }

        onPerformSearch(
          search.search_term,
          search.filters?.search_type || null, // Use stored search_type
          search.filters?.search_value || null, // Use stored search_value
          geocodedLat || null,
          geocodedLng || null,
          geocodedAddress || null,
        );
      } else {
        // Otherwise, set params and navigate (index route)
        let geocodedLat: number | undefined;
        let geocodedLng: number | undefined;
        let geocodedAddress: string | undefined;

        // Use stored coordinates if available, otherwise geocode
        if (search.filters?.latitude && search.filters?.longitude) {
          // Use stored coordinates from recent search
          geocodedLat = parseFloat(search.filters.latitude);
          geocodedLng = parseFloat(search.filters.longitude);
          geocodedAddress =
            search.filters.address || search.filters.placesToReplaceString;
        } else if (search.search_term && !isVenueRecentSearch) {
          // Geocode only if coordinates not stored (backwards compatibility)
          try {
            const { lat: safeLat, lng: safeLng } = getSafeCoordinates();
            const { lat, lng, address, error } =
              await getFirstResultLocationAndAddress(
                search.search_term,
                safeLat,
                safeLng,
              );

            if (!error) {
              geocodedLat = lat;
              geocodedLng = lng;
              geocodedAddress = address;
            }
          } catch (error) {
            console.error("Error geocoding search term:", error);
          }
        }

        // Update home search params
        setSearchPageParams((prev) => {
          const openingHoursModeValue = search.filters?.open_now
            ? "open_now"
            : search.filters?.opening_hours?.day?.length
              ? "custom"
              : "any";

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const searchParams: any = {
            ...prev,
            search: search.search_term,
            types: typesStr,
            accessibilities: accessibilitiesStr,
            // Saved searches store tags only (D0-2); never carry a section selection over.
            accessibility_sections: "",
            // Use stored or geocoded coordinates
            ...(geocodedLat && { latitude: geocodedLat.toString() }),
            ...(geocodedLng && { longitude: geocodedLng.toString() }),
            ...(geocodedAddress && { placesToReplaceString: geocodedAddress }),
            // Clear the stale viewport bbox so this new location isn't filtered by the old area.
            map_ne_lat: undefined,
            map_ne_lng: undefined,
            map_sw_lat: undefined,
            map_sw_lng: undefined,
            showVenueOptions: getShowVenueOptions(),
            openingHoursMode: openingHoursModeValue,
            openingHoursDay:
              search.filters?.opening_hours?.day?.[0]?.toString(),
            openingHoursTime: search.filters?.opening_hours?.start,
            autoZoom: "yes", // Enable dynamic zoom for recent searches
            unixTimestamp: Math.floor(Date.now() / 1000),
            // Restore search context from stored filters
            ...(search.filters?.search_type && {
              search_type: search.filters.search_type,
            }),
            ...(search.filters?.search_value && {
              search_value: search.filters.search_value,
            }),
            // The search was initiated from a recent-search pill, regardless of the stored query
            // kind -> canonical search_method (read by place_search_results_viewed on /search).
            search_method: "recent_search",

            //taggign mode to be picked based on the mapper status - existing value set
            // ...(search.filters?.tagging_mode && { tagging_mode: search.filters.tagging_mode }),
          };

          //  // Clear search_type and search_value for keyword searches (only set when suggestion is selected)
          //  if (prev.search_type) delete searchParams.search_type;
          //  if (prev.search_value) delete searchParams.search_value;

          //  // Only set search_type for nearby search (when no search term and no filters)
          //  if (!search.search_term && !typesStr) {
          //    searchParams.search_type = "nearby";
          //    searchParams.search_value = "Nearby";
          //  }

          return searchParams;
        });

        // Update mapCenter atom if we have coordinates
        // This prevents BasicMapView from using stale mapCenter
        if (geocodedLat && geocodedLng) {
          setMapCenter({
            lat: geocodedLat,
            lng: geocodedLng,
          });
          console.log(
            "SearchBar: Updated mapCenter for recent search:",
            geocodedLat,
            geocodedLng,
          );
        }

        // Navigate to search page
        navigate({ to: "/search" });
      }

      onRecentSearchSelect?.(search);

      // Close overlay if in overlay mode (after all state updates)
      if (layout === "overlay" || layout === "overlaymobile") {
        onOverlayClose?.();
      }
    },
    [
      layout,
      onOverlayClose,
      setSearchPageParams,
      setMapCenter,
      navigate,
      onRecentSearchSelect,
      onPerformSearch,
      getSafeCoordinates,
      locationText,
      setInternalSearchTerm,
      setSelectedVenue,
      setLocationSearchAnchor,
    ],
  );

  // Handle neighborhood selection
  const handleNeighborhoodSelect = useCallback(
    async (neighborhood: NeighborhoodSuggestion) => {
      // neighborhood tap -> place_search_performed (search_method=neighborhood). selected_filter =
      // business-type names + resolved accessibility names from the neighborhood's own filters.
      const nbhdAccNameMap = new Map<string, string>();
      (accessibilityTagsData ?? []).forEach((o) =>
        o?.accessibility_tags?.forEach((t) =>
          nbhdAccNameMap.set(String(t.id), t.name),
        ),
      );
      const nbhdBusinessTypes = Array.isArray(
        neighborhood.filters?.business_types,
      )
        ? neighborhood.filters.business_types
        : neighborhood.filters?.business_types
          ? [neighborhood.filters.business_types]
          : [];
      const nbhdAccNames = (
        Array.isArray(neighborhood.filters?.accessibilities)
          ? neighborhood.filters.accessibilities
          : neighborhood.filters?.accessibilities
            ? [neighborhood.filters.accessibilities]
            : []
      )
        .map((id) => nbhdAccNameMap.get(String(id)))
        .filter((n): n is string => Boolean(n));
      const nbhdSelected = [...nbhdBusinessTypes, ...nbhdAccNames];
      trackEvent("place_search_performed", {
        brand_id: "",
        brand_name: "",
        business_id: "",
        place_name: "",
        filters_count: nbhdSelected.length,
        search_method: "neighborhood",
        search_query: neighborhood.name ?? "",
        search_result_type: "neighborhood",
        selected_filter: nbhdSelected,
      });

      // Update internal search term
      setInternalSearchTerm(neighborhood.name);

      setSelectedVenue(null);
      setLocationSearchAnchor(null);

      // Build filter strings from the neighborhood
      // Handle both string and array formats for business_types
      const typesStr = Array.isArray(neighborhood.filters?.business_types)
        ? neighborhood.filters.business_types.join("|")
        : neighborhood.filters?.business_types || "";

      // Handle both string and array formats for accessibilities
      const accessibilitiesStr = Array.isArray(
        neighborhood.filters?.accessibilities,
      )
        ? neighborhood.filters.accessibilities.join("|")
        : neighborhood.filters?.accessibilities || "";

      // If onPerformSearch is provided (search route), call it directly
      if (onPerformSearch && neighborhood.name) {
        // For keyword searches, call search-places to geocode if no location provided
        let geocodedLat: number | undefined;
        let geocodedLng: number | undefined;
        let geocodedAddress: string | undefined;

        // CheckIcon if neighborhood already has location coordinates
        if (
          !neighborhood.filters?.search_latitude ||
          !neighborhood.filters?.search_longitude
        ) {
          try {
            const { lat: safeLat, lng: safeLng } = getSafeCoordinates();
            const { lat, lng, address, error } =
              await getFirstResultLocationAndAddress(
                neighborhood.name,
                safeLat,
                safeLng,
              );

            if (!error) {
              geocodedLat = lat;
              geocodedLng = lng;
              geocodedAddress = address;
            }
          } catch (error) {
            console.error("Error geocoding neighborhood:", error);
          }
        }

        // Use neighborhood coords if available, otherwise use geocoded
        const lat =
          neighborhood.filters?.search_latitude || geocodedLat || null;
        const lng =
          neighborhood.filters?.search_longitude || geocodedLng || null;
        const address = geocodedAddress || null;

        // Update mapCenter atom before calling performSearch
        // This prevents BasicMapView from using stale mapCenter and triggering unwanted updates
        if (lat !== null && lng !== null) {
          setMapCenter({
            lat: lat,
            lng: lng,
          });
          console.log(
            "SearchBar: Updated mapCenter for neighborhood on search page:",
            lat,
            lng,
          );
        }

        onPerformSearch(
          neighborhood.name,
          null,
          null,
          lat,
          lng,
          address,
          "neighborhood",
        );
      } else {
        // Otherwise, set params and navigate (index route)
        // For keyword searches, call search-places to geocode if no location provided
        let geocodedLat: number | undefined;
        let geocodedLng: number | undefined;
        let geocodedAddress: string | undefined;

        // CheckIcon if neighborhood already has location coordinates
        if (
          !neighborhood.filters?.search_latitude ||
          !neighborhood.filters?.search_longitude
        ) {
          try {
            const { lat: safeLat, lng: safeLng } = getSafeCoordinates();
            const { lat, lng, address, error } =
              await getFirstResultLocationAndAddress(
                neighborhood.name,
                safeLat,
                safeLng,
              );

            if (!error) {
              geocodedLat = lat;
              geocodedLng = lng;
              geocodedAddress = address;
            }
          } catch (error) {
            console.error("Error geocoding neighborhood:", error);
          }
        }

        // Update home search params
        setSearchPageParams((prev) => ({
          ...prev,
          search: neighborhood.name,
          types: typesStr,
          accessibilities: accessibilitiesStr,
          accessibility_sections: "",
          // Use neighborhood location if available, otherwise use geocoded
          latitude:
            neighborhood.filters?.search_latitude?.toString() ||
            (geocodedLat ? geocodedLat.toString() : undefined),
          longitude:
            neighborhood.filters?.search_longitude?.toString() ||
            (geocodedLng ? geocodedLng.toString() : undefined),
          ...(geocodedAddress && { placesToReplaceString: geocodedAddress }),
          // Clear the stale viewport bbox so this new location isn't filtered by the old area.
          map_ne_lat: undefined,
          map_ne_lng: undefined,
          map_sw_lat: undefined,
          map_sw_lng: undefined,
          autoZoom: "yes", // Enable dynamic zoom for neighborhood searches
          // search_method = the entry PATH; performSearch preserves it on /search (ENTRY_PATH_METHODS).
          search_method: "neighborhood",
          unixTimestamp: Math.floor(Date.now() / 1000),
        }));

        // Navigate to search page
        navigate({ to: "/search" });
      }

      // Navigate to search page
      navigate({ to: "/search" });

      onNeighborhoodSelect?.(neighborhood);

      // Close overlay if in overlay mode (after all state updates)
      if (layout === "overlay" || layout === "overlaymobile") {
        onOverlayClose?.();
      }
    },
    [
      layout,
      onOverlayClose,
      setSearchPageParams,
      navigate,
      onNeighborhoodSelect,
      onPerformSearch,
      getSafeCoordinates,
      setInternalSearchTerm,
      setSelectedVenue,
      setLocationSearchAnchor,
    ],
  );

  // Handle login press
  const handleLoginPress = useCallback(() => {
    onLoginPress?.();
  }, [navigate, onLoginPress]);

  // Handle location modal open
  const handleOpenChangeLocationModal = useCallback(() => {
    setIsChangeLocationModalOpen(true);
  }, []);

  // Handle location modal close
  const handleCloseChangeLocationModal = useCallback(() => {
    setIsChangeLocationModalOpen(false);
    // Return focus to location button after modal closes
    setTimeout(() => {
      locationButtonRef.current?.focus();
    }, 100);
  }, []);

  // Handle location select
  const handleLocationSelect = useCallback(
    (location: SelectedLocation) => {
      if (onLocationChange) {
        onLocationChange(location);
      }
    },
    [onLocationChange],
  );

  // Handle use precise location click
  const handleUsePreciseLocationClickInternal = useCallback(() => {
    onUsePreciseLocationClick?.();
  }, [onUsePreciseLocationClick]);

  // Computed accessibility count
  // Use filtersState.accessibilityOptions from useFilters hook (updated by AccessibilityFeatures)
  // This is the source of truth when AccessibilityFeatures component is used in the accordion
  // If accessibilityCount prop is provided, use that (highest priority)
  // Otherwise, use filtersState.accessibilityOptions if available, else currentAccessibilityOptions
  // The panel's section counts summed: a section-less key is a tick in every section listing it.
  const accessibilityOptionsFromFilters = useMemo(() => selectionOf(filtersState), [filtersState]);
  // The mobile layout draws the filter sheet, which picks its own list, not the panel data passed in.
  const displayedAccessibilityCount = useAccessibilitySelectionCount(
    accessibilityOptionsFromFilters.length > 0
      ? accessibilityOptionsFromFilters
      : currentAccessibilityOptions,
    layout === "overlaymobile" ? undefined : (filterSectionsData ?? accessibilityTagsData),
  );
  const computedAccessibilityCount =
    accessibilityCount ?? displayedAccessibilityCount;

  // Computed business type count
  const computedBusinessTypeCount =
    businessTypeCount ?? currentSelectedBusinessTypes.length;

  // Total filter count (accessibility + business type + opening hours)
  const totalFilterCount =
    computedAccessibilityCount +
    computedBusinessTypeCount +
    (currentOpeningHoursMode !== "any" ? 1 : 0);

  const content = (
    <>
      {/* Screen Reader Status Announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {statusMessage}
      </div>

      {/* Search Input Container */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-6 items-stretch md:items-center w-full">
        <div className="flex-1 flex flex-col md:flex-row items-stretch md:items-center bg-bg-neutral-secondary-default border border-border-token-neutral-subtle rounded-lg min-h-[48px] md:min-h-[56px]">
          {/* Search Input / Button */}
          {inputMode === "input" ? (
            <div className="flex-1 min-w-0">
              <AutoSuggestInput
                label={i18n.get("searchVenueOrLocation")}
                value={searchTerm}
                onValueChange={handleSearchChange}
                onSubmit={() => {
                  closeAllPanels();
                  handleSearchSubmit();
                }}
                onSelectUnified={(item, sessionToken) => {
                  closeAllPanels();
                  handleUnifiedSelect(item, sessionToken);
                }}
                onClear={() => {
                  handleClearSearch();
                }}
                latitude={getSafeCoordinates().lat}
                longitude={getSafeCoordinates().lng}
                radiusKm={radiusKm}
                tagging_mode={tagging_mode}
                types={types}
                accessibilities={accessibilities}
                accessibilitySections={accessibilitySections}
                showVenueOptions={showVenueOptions}
                showSearchSuggestions={showSearchSuggestions}
                onNearbyPlacesClick={handleNearbyPlacesClick}
                onRecentSearchSelect={handleRecentSearchSelect}
                onNeighborhoodSelect={handleNeighborhoodSelect}
                onLoginPress={handleLoginPress}
                suppressDropdown={
                  (layout === "overlay" || layout === "overlaymobile") &&
                  isSavedSearchesOpenState
                }
                className="w-full"
                layout={layout}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={handleInputFocus}
              className="flex-1 flex items-center justify-between md:max-w-[480px] pl-4 pr-3 py-3 md:py-2 cursor-pointer"
            >
              <span
                className={cn(
                  "label-m-medium truncate text-left",
                  searchTerm
                    ? "text-fg-neutral-strong"
                    : "text-fg-neutral-subtle",
                )}
              >
                {searchTerm || i18n.get("searchVenueOrLocation")}
              </span>
            </button>
          )}

          {/* Filters - Hidden on mobile, shown on tablet and up */}
          <div className="hidden md:flex items-center">
            {/* Divider */}
            <div
              className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
              aria-hidden="true"
            />

            {/* Accessibility Filter - Wrapped with panel */}
            <div className="relative">
              <button
                type="button"
                onClick={handleAccessibilityClick}
                onMouseDown={() => {
                  accessibilityWasOpenRef.current =
                    activePanel === "accessibility";
                }}
                className={cn(
                  "flex items-center gap-2 lg:gap-4 px-3 lg:px-4 py-2 transition-colors focus-blue border-t md:border-t-0 border-border-token-neutral-minimal",
                  activePanel === "accessibility"
                    ? "bg-container-brand-minimal"
                    : "hover:bg-bg-neutral-secondary-hover",
                )}
                aria-label={
                  computedAccessibilityCount > 0
                    ? `${i18n.get("accessibility")}, ${computedAccessibilityCount} ${computedAccessibilityCount === 1 ? "filter" : "filters"} applied`
                    : `${i18n.get("accessibility")} Filter`
                }
                aria-expanded={activePanel === "accessibility"}
                aria-haspopup="dialog"
              >
                <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                  {i18n.get("accessibility")}
                </span>
                {computedAccessibilityCount > 0 ? (
                  <span
                    className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                    aria-hidden="true"
                  >
                    {computedAccessibilityCount}
                  </span>
                ) : (
                  <ChevronDown
                    size={16}
                    className={cn(
                      "text-fg-neutral-subtle transition-transform",
                      activePanel === "accessibility" && "rotate-180",
                    )}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                )}
              </button>

              {/* Accessibility Panel - positioned below button, left aligned */}
              {activePanel === "accessibility" && (
                <div className="absolute left-0 top-full mt-2 z-50">
                  <AccessibilityFilterPanel
                    isOpen={true}
                    onClose={() => dismissFilterPanel("accessibility")}
                    selectedOptions={currentAccessibilityOptions}
                    onOptionsChange={handleAccessibilityOptionsChange}
                    onClearAll={handleAccessibilityClearAll}
                    accessibilityTagsData={filterSectionsData ?? accessibilityTagsData}
                  />
                </div>
              )}
            </div>

            {/* Divider */}
            <div
              className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
              aria-hidden="true"
            />

            {/* Business Type Filter - Wrapped with panel */}
            <div className="relative" onBlur={handleFilterPanelBlur}>
              <button
                type="button"
                onClick={handleBusinessTypeClick}
                onMouseDown={() => {
                  businessTypeWasOpenRef.current =
                    activePanel === "businessType";
                }}
                className={cn(
                  "flex items-center gap-2 lg:gap-4 px-3 lg:px-4 py-2 transition-colors focus-blue border-t md:border-t-0 border-border-token-neutral-minimal",
                  activePanel === "businessType"
                    ? "bg-container-brand-minimal"
                    : "hover:bg-bg-neutral-secondary-hover",
                )}
                aria-label={
                  computedBusinessTypeCount > 0
                    ? `${i18n.get("businessType")}, ${computedBusinessTypeCount} ${computedBusinessTypeCount === 1 ? "filter" : "filters"} applied`
                    : `${i18n.get("businessType")} Filter`
                }
                aria-expanded={activePanel === "businessType"}
                aria-haspopup="listbox"
              >
                <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                  {i18n.get("businessType")}
                </span>
                {computedBusinessTypeCount > 0 ? (
                  <span
                    className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                    aria-hidden="true"
                  >
                    {computedBusinessTypeCount}
                  </span>
                ) : (
                  <ChevronDown
                    size={16}
                    className={cn(
                      "text-fg-neutral-subtle transition-transform",
                      activePanel === "businessType" && "rotate-180",
                    )}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                )}
              </button>

              {/* Business Type Panel - positioned below button, left aligned */}
              {activePanel === "businessType" && (
                <div className="absolute left-0 top-full mt-2 z-50">
                  <BusinessTypeDropdown
                    isOpen={true}
                    onClose={() => dismissFilterPanel("business_type")}
                    businessTypes={businessTypes}
                    selectedTypes={currentSelectedBusinessTypes}
                    onSelectionChange={handleBusinessTypesChange}
                    onTypeToggle={handleBusinessTypeToggle}
                    onClearAll={() =>
                      trackEvent("filter_cleared", {
                        filters_count: buildFilterSummary().filters_count,
                      })
                    }
                  />
                </div>
              )}
            </div>

            {/* Divider */}
            <div
              className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
              aria-hidden="true"
            />

            {/* Opening Hour Filter - Wrapped with panel */}
            <div className="relative" onBlur={handleFilterPanelBlur}>
              <button
                type="button"
                onClick={handleOpeningHourClick}
                onMouseDown={() => {
                  openingHoursWasOpenRef.current =
                    activePanel === "openingHours";
                }}
                className={cn(
                  "flex items-center gap-2 lg:gap-4 px-3 lg:px-4 pr-3 py-2 transition-colors focus-blue border-t md:border-t-0 border-border-token-neutral-minimal",
                  activePanel === "openingHours"
                    ? "bg-container-brand-minimal"
                    : "hover:bg-bg-neutral-secondary-hover",
                )}
                aria-label={
                  currentOpeningHoursMode !== "any"
                    ? `${i18n.get("openingHours")}, 1 filter applied`
                    : `${i18n.get("openingHours")} Filter`
                }
                aria-expanded={activePanel === "openingHours"}
                aria-haspopup="dialog"
              >
                <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                  {i18n.get("openingHours")}
                </span>
                {currentOpeningHoursMode !== "any" ? (
                  <span
                    className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                    aria-hidden="true"
                  >
                    1
                  </span>
                ) : (
                  <ChevronDown
                    size={16}
                    className={cn(
                      "text-fg-neutral-subtle transition-transform",
                      activePanel === "openingHours" && "rotate-180",
                    )}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                )}
              </button>

              {/* Opening Hours Panel - positioned below button, right aligned */}
              {activePanel === "openingHours" && (
                <div className="absolute right-0 top-full mt-2 z-50">
                  <OpeningHoursFilterPanel
                    isOpen={true}
                    onClose={() => dismissFilterPanel("opening_hours")}
                    selectedMode={currentOpeningHoursMode}
                    onModeChange={handleOpeningHoursModeChange}
                    selectedDay={currentSelectedDay}
                    onDayChange={handleDayChange}
                    selectedTime={currentSelectedTime}
                    onTimeChange={handleTimeChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Button */}
        <button
          type="button"
          onClick={handleSearchSubmit}
          className={cn(
            "flex items-center justify-center min-h-[44px] md:min-h-[40px] min-w-[44px] px-4 py-3 md:py-2",
            "bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover",
            "rounded-lg transition-colors focus-blue h-[56px]",
          )}
        >
          <span className="label-m-medium text-white">
            {i18n.get("search")}
          </span>
        </button>
      </div>

      {/* Bottom Row: Saved Search + Recent Searches */}
      <div className="flex items-center gap-3 w-full">
        {/* Saved Searches Combobox */}
        {savedSearchOptions.length >= 0 && (
          <Combobox
            options={savedSearchOptions}
            value=""
            onValueChange={handleSavedSearchSelectFromCombobox}
            placeholder={`${i18n.get("applySavedSearches")} (${savedSearchesCount})`}
            ariaLabel={`${i18n.get("applySavedSearches")} (${savedSearchesCount})`}
            emptyText={i18n.get("noSavedSearches") || "No saved searches yet"}
            showSearch={false}
            searchPlaceholder={i18n.get("search") || "Search..."}
            className="w-full md:w-[250px]"
            showBorderOnTrigger={false}
          />
        )}

        {/* handleClearAllFilters */}
        {/* Divider */}
        {totalFilterCount > 0 && (
          <>
            <div
              className="h-[24px] w-px border border-border-token-neutral-minimal"
              aria-hidden="true"
            />
            {/* Filter Icon - Primary color when filters are active */}
            <SlidersHorizontalIcon
              size={24}
              className="text-fg-neutral-strong"
              strokeWidth={1.5}
              aria-hidden="true"
            />

            <button
              type="button"
              onClick={handleClearAllFilters}
              className="label-m-medium text-fg-neutral-strong hover:opacity-80 transition-opacity focus-blue whitespace-nowrap"
              aria-label={i18n.get("resetFilters") || "Reset all filters"}
            >
              {i18n.get("resetFilters")}
            </button>
          </>
        )}
      </div>
    </>
  );

  const contentMobile = (
    <div className="flex flex-col gap-3 w-full">
      {/* Segmented Control */}
      <div
        className="flex items-center gap-1 p-1 rounded-md border border-border-token-brand-minimal"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeSegment === "new"}
          onClick={() => {
            setActiveSegment("new");
            if (isSavedSearchesOpenState) {
              handleSavedSearchesClick();
            }
          }}
          className={cn(
            "flex-1 flex items-center justify-center min-h-[36px] px-4 py-2 rounded-md transition-colors focus-blue",
            "label-m-medium",
            activeSegment === "new"
              ? "bg-bg-brand-primary-default text-white"
              : "text-fg-neutral-subtle hover:text-fg-neutral-strong",
          )}
        >
          {i18n.get("newSearch")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSegment === "saved"}
          aria-label={`${i18n.get("savedSearches")}, ${savedSearchesCount} ${savedSearchesCount === 1 ? "search" : "searches"}`}
          onClick={() => {
            setActiveSegment("saved");
            if (!isSavedSearchesOpenState) {
              handleSavedSearchesClick();
            }
          }}
          className={cn(
            "flex-1 flex items-center justify-center min-h-[36px] px-4 py-2 rounded-md transition-colors focus-blue",
            "label-m-medium",
            activeSegment === "saved"
              ? "bg-bg-brand-primary-default text-white"
              : "text-fg-neutral-subtle hover:text-fg-neutral-strong",
          )}
        >
          {i18n.get("savedSearches")} ({savedSearchesCount})
        </button>
      </div>

      {/* Conditional Content Based on Active Segment */}
      {activeSegment === "new" ? (
        <>
          {/* Search Input */}
          <div className="flex items-center bg-bg-neutral-secondary-default border border-border-token-neutral-subtle rounded-lg min-h-[48px] px-4">
            <MagnifyingGlassIcon
              size={20}
              className="text-fg-neutral-subtle flex-shrink-0"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            {inputMode === "input" ? (
              <AutoSuggestInput
                label={i18n.get("searchVenueOrLocation")}
                value={searchTerm}
                onValueChange={handleSearchChange}
                onSubmit={() => {
                  closeAllPanels();
                  handleSearchSubmit();
                }}
                onSelectUnified={(item, sessionToken) => {
                  closeAllPanels();
                  handleUnifiedSelect(item, sessionToken);
                }}
                onClear={() => {
                  handleClearSearch();
                }}
                latitude={getSafeCoordinates().lat}
                longitude={getSafeCoordinates().lng}
                radiusKm={radiusKm}
                tagging_mode={tagging_mode}
                types={types}
                accessibilities={accessibilities}
                accessibilitySections={accessibilitySections}
                showVenueOptions={showVenueOptions}
                showSearchSuggestions={showSearchSuggestions}
                onNearbyPlacesClick={handleNearbyPlacesClick}
                onRecentSearchSelect={handleRecentSearchSelect}
                onNeighborhoodSelect={handleNeighborhoodSelect}
                onLoginPress={handleLoginPress}
                suppressDropdown={
                  (layout === "overlay" || layout === "overlaymobile") &&
                  isSavedSearchesOpenState
                }
                className="flex-1 min-w-0"
              />
            ) : (
              <button
                type="button"
                onClick={handleInputFocus}
                className="flex-1 flex items-center justify-start min-h-[48px] cursor-pointer"
              >
                <span
                  className={cn(
                    "label-m-medium truncate text-left",
                    searchTerm
                      ? "text-fg-neutral-strong"
                      : "text-fg-neutral-subtle",
                  )}
                >
                  {searchTerm || i18n.get("searchVenueOrLocation")}
                </span>
              </button>
            )}
          </div>

          {/* Filters Button */}
          <button
            type="button"
            onClick={() => {
              setIsFiltersExpanded(!isFiltersExpanded);
            }}
            className="flex items-center justify-between w-full min-h-[48px] px-4 bg-bg-neutral-secondary-default border border-border-token-neutral-subtle rounded-lg hover:bg-bg-neutral-secondary-hover transition-colors focus-blue"
            aria-label={
              totalFilterCount > 0
                ? `${i18n.get("filters")}, ${totalFilterCount} ${totalFilterCount === 1 ? "filter" : "filters"} applied`
                : i18n.get("filters")
            }
            aria-expanded={isFiltersExpanded}
          >
            <div className="flex items-center gap-3">
              <SlidersHorizontalIcon
                size={20}
                className={cn(
                  totalFilterCount > 0
                    ? "text-fg-neutral-strong"
                    : "text-fg-neutral-subtle",
                )}
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <span className="label-m-medium text-fg-neutral-subtle">
                {i18n.get("filters")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {totalFilterCount > 0 && (
                <span
                  className="flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                  aria-hidden="true"
                >
                  {totalFilterCount}
                </span>
              )}
              <ChevronRight
                size={20}
                className={cn(
                  "text-fg-neutral-subtle transition-transform flex-shrink-0",
                  isFiltersExpanded && "rotate-90",
                )}
                aria-hidden="true"
              />
            </div>
          </button>

          {/* Filters Container - Accordions */}
          {isFiltersExpanded && (
            <div className="w-full max-h-[60vh] overflow-y-auto bg-bg-neutral-secondary-default border border-border-token-neutral-minimal rounded-lg p-4">
              <Accordion
                type="single"
                collapsible
                value={expandedAccordion || undefined}
                onValueChange={(value) => {
                  setExpandedAccordion(value || null);
                }}
                className="w-full"
              >
                {/* Accessibility Filter Accordion */}
                <AccordionItem
                  value="accessibility"
                  className="border-none pb-4"
                >
                  <AccordionTrigger
                    className={cn(
                      "px-3 lg:px-4 py-2 hover:no-underline focus-blue",
                      computedAccessibilityCount > 0
                        ? "bg-container-brand-minimal"
                        : "",
                    )}
                    aria-label={
                      computedAccessibilityCount > 0
                        ? `${i18n.get("accessibility")}, ${computedAccessibilityCount} ${computedAccessibilityCount === 1 ? "filter" : "filters"} applied`
                        : `${i18n.get("accessibility")} Filter`
                    }
                  >
                    <div className="flex items-center gap-2 lg:gap-4 w-full">
                      <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                        {i18n.get("accessibility")}
                      </span>
                      {computedAccessibilityCount > 0 && (
                        <span
                          className="flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                          aria-hidden="true"
                        >
                          {computedAccessibilityCount}
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pt-0 pb-4">
                    <div>
                      <AccessibilityFeaturesContextAware />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Business Type Filter Accordion */}
                <AccordionItem
                  value="businessType"
                  className="pb-4 border-none border-t border-border-token-neutral-minimal"
                >
                  <AccordionTrigger
                    className={cn(
                      "px-3 lg:px-4 py-2 hover:no-underline focus-blue",
                      computedBusinessTypeCount > 0
                        ? "bg-container-brand-minimal"
                        : "",
                    )}
                    aria-label={
                      computedBusinessTypeCount > 0
                        ? `${i18n.get("businessType")}, ${computedBusinessTypeCount} ${computedBusinessTypeCount === 1 ? "filter" : "filters"} applied`
                        : `${i18n.get("businessType")} Filter`
                    }
                  >
                    <div className="flex items-center gap-2 lg:gap-4 w-full">
                      <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                        {i18n.get("businessType")}
                      </span>
                      {computedBusinessTypeCount > 0 && (
                        <span
                          className="flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                          aria-hidden="true"
                        >
                          {computedBusinessTypeCount}
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pt-0 pb-4">
                    <div>
                      {/* Business Type Tags Grid */}
                      <div
                        className="flex flex-wrap gap-2"
                        role="listbox"
                        aria-label={i18n.get("businessTypes")}
                        aria-multiselectable="true"
                      >
                        {businessTypesList.map((item) => {
                          const isSelected =
                            currentSelectedBusinessTypes.includes(item.name);
                          return (
                            <button
                              key={item.name}
                              type="button"
                              onClick={() => {
                                handleBusinessTypeToggle(item.name);
                              }}
                              className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-full transition-colors focus-blue",
                                isSelected
                                  ? "bg-secondary-400"
                                  : "bg-container-brand-minimal",
                              )}
                              role="option"
                              aria-selected={isSelected}
                              aria-label={
                                isSelected ? `${item.name} selected` : item.name
                              }
                            >
                              {/* Business Type Icon */}
                              <div
                                className={cn(
                                  "w-4 h-4 flex-shrink-0",
                                  isDark
                                    ? isSelected
                                      ? "filter brightness-100"
                                      : "filter brightness-[10]"
                                    : "filter brightness-100",
                                )}
                              >
                                <img
                                  src={bucketUrlPrefix + item.image}
                                  alt=""
                                  className="w-full h-full object-contain"
                                  aria-hidden="true"
                                />
                              </div>

                              {/* Business Type Name */}
                              <span
                                className={cn(
                                  "label-s-medium whitespace-nowrap",
                                  isSelected
                                    ? "text-fg-neutral-strong"
                                    : "text-fg-neutral-strong",
                                )}
                              >
                                {item.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {businessTypesList.length === 0 && (
                        <p className="text-center py-8 label-m-medium text-fg-neutral-subtle">
                          {i18n.get("noBusinessTypesAvailable")}
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Opening Hours Filter Accordion */}
                <AccordionItem
                  value="openingHours"
                  className="border-none border-t border-border-token-neutral-minimal"
                >
                  <AccordionTrigger
                    className={cn(
                      "px-3 lg:px-4 py-2 hover:no-underline focus-blue",
                      currentOpeningHoursMode !== "any"
                        ? "bg-container-brand-minimal"
                        : "",
                    )}
                    aria-label={
                      currentOpeningHoursMode !== "any"
                        ? `${i18n.get("openingHours")}, 1 filter applied`
                        : `${i18n.get("openingHours")} Filter`
                    }
                  >
                    <div className="flex items-center gap-2 lg:gap-4 w-full">
                      <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                        {i18n.get("openingHours")}
                      </span>
                      {currentOpeningHoursMode !== "any" && (
                        <span
                          className="flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                          aria-hidden="true"
                        >
                          1
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pt-0 pb-4">
                    <div>
                      {/* Mode Toggle */}
                      <div className="flex flex-col gap-2 py-2">
                        <div
                          className={cn(
                            "flex items-center gap-1 p-1",
                            "border border-border-token-brand-minimal rounded-full",
                          )}
                          role="tablist"
                          aria-label={i18n.get("selectOpeningHoursMode")}
                        >
                          {[
                            {
                              id: "any" as OpeningHoursMode,
                              label: i18n.get("any"),
                            },
                            {
                              id: "open_now" as OpeningHoursMode,
                              label: i18n.get("openNow"),
                            },
                            {
                              id: "custom" as OpeningHoursMode,
                              label: i18n.get("custom"),
                            },
                          ].map((mode) => {
                            const isSelected =
                              currentOpeningHoursMode === mode.id;
                            return (
                              <button
                                key={mode.id}
                                type="button"
                                role="tab"
                                aria-selected={isSelected}
                                onClick={() => {
                                  handleOpeningHoursModeChange(mode.id);
                                }}
                                className={cn(
                                  "flex-1 flex items-center justify-center min-h-[36px] min-w-[44px] px-4 py-2 rounded-full transition-colors focus-blue",
                                  "label-m-medium whitespace-nowrap",
                                  isSelected
                                    ? "bg-bg-brand-primary-default text-white"
                                    : "text-fg-neutral-strong hover:bg-bg-neutral-secondary-hover",
                                )}
                              >
                                {mode.label}
                              </button>
                            );
                          })}
                        </div>

                        {/* Custom Mode Options */}
                        {currentOpeningHoursMode === "custom" && (
                          <div className="flex flex-col gap-2 mt-2">
                            {/* Day Selector */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsDayDropdownOpen(!isDayDropdownOpen);
                                  setIsTimeDropdownOpen(false);
                                }}
                                className={cn(
                                  "w-full flex items-center justify-between min-h-[56px] px-4 py-2",
                                  "bg-bg-neutral-secondary-default border border-border-token-neutral-subtle rounded-lg",
                                  "hover:bg-bg-neutral-secondary-hover transition-colors focus-blue",
                                )}
                                aria-haspopup="listbox"
                                aria-expanded={isDayDropdownOpen}
                              >
                                <span className="label-m-medium text-fg-neutral-subtle">
                                  {currentSelectedDay || i18n.get("selectDay")}
                                </span>
                                <ChevronDown
                                  size={24}
                                  className={cn(
                                    "text-fg-neutral-subtle transition-transform",
                                    isDayDropdownOpen && "rotate-180",
                                  )}
                                  aria-hidden="true"
                                />
                              </button>

                              {/* Day Dropdown */}
                              {isDayDropdownOpen && (
                                <div
                                  className={cn(
                                    "absolute top-full left-0 right-0 mt-1 z-10",
                                    "bg-bg-neutral-secondary-default border border-border-token-neutral-minimal",
                                    "rounded-lg shadow-lg max-h-[200px] overflow-y-auto",
                                  )}
                                  role="listbox"
                                  aria-label={i18n.get("selectDay")}
                                >
                                  {DAYS_OF_WEEK.map((day) => (
                                    <button
                                      key={day}
                                      type="button"
                                      role="option"
                                      aria-selected={currentSelectedDay === day}
                                      onClick={() => {
                                        handleDayChange(day);
                                        setIsDayDropdownOpen(false);
                                      }}
                                      className={cn(
                                        "w-full text-left px-4 py-3 label-m-medium",
                                        "hover:bg-bg-neutral-secondary-hover transition-colors focus-blue",
                                        currentSelectedDay === day
                                          ? "text-fg-brand-subtle bg-container-brand-minimal"
                                          : "text-fg-neutral-subtle",
                                      )}
                                    >
                                      {day}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Time Selector */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsTimeDropdownOpen(!isTimeDropdownOpen);
                                  setIsDayDropdownOpen(false);
                                }}
                                className={cn(
                                  "w-full flex items-center justify-between min-h-[56px] px-4 py-2",
                                  "bg-bg-neutral-secondary-default border border-border-token-neutral-subtle rounded-lg",
                                  "hover:bg-bg-neutral-secondary-hover transition-colors focus-blue",
                                )}
                                aria-haspopup="listbox"
                                aria-expanded={isTimeDropdownOpen}
                              >
                                <span className="label-m-medium text-fg-neutral-subtle">
                                  {currentSelectedTime ||
                                    i18n.get("selectTime")}
                                </span>
                                <ChevronDown
                                  size={24}
                                  className={cn(
                                    "text-fg-neutral-subtle transition-transform",
                                    isTimeDropdownOpen && "rotate-180",
                                  )}
                                  aria-hidden="true"
                                />
                              </button>

                              {/* Time Dropdown */}
                              {isTimeDropdownOpen && (
                                <div
                                  className={cn(
                                    "absolute top-full left-0 right-0 mt-1 z-10",
                                    "bg-bg-neutral-secondary-default border border-border-token-neutral-minimal",
                                    "rounded-lg shadow-lg max-h-[200px] overflow-y-auto",
                                  )}
                                  role="listbox"
                                  aria-label={i18n.get("selectTime")}
                                >
                                  {TIME_OPTIONS.map((time) => (
                                    <button
                                      key={time}
                                      type="button"
                                      role="option"
                                      aria-selected={
                                        currentSelectedTime === time
                                      }
                                      onClick={() => {
                                        handleTimeChange(time);
                                        setIsTimeDropdownOpen(false);
                                      }}
                                      className={cn(
                                        "w-full text-left px-4 py-3 label-m-medium",
                                        "hover:bg-bg-neutral-secondary-hover transition-colors focus-blue",
                                        currentSelectedTime === time
                                          ? "text-fg-brand-subtle bg-container-brand-minimal"
                                          : "text-fg-neutral-subtle",
                                      )}
                                    >
                                      {time}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}

          {/* Action Buttons: Reset and Search */}
          <div className="flex items-center gap-3 w-full">
            {/* Reset Button - conditionally rendered */}
            {totalFilterCount > 0 && (
              <button
                type="button"
                onClick={handleClearAllFilters}
                className={cn(
                  "flex items-center justify-center min-h-[48px] px-4 py-2 rounded-lg",
                  "label-s-medium text-fg-neutral-strong",
                  "border border-border-token-brand-minimal",
                  "hover:bg-bg-brand-secondary-hover/20",
                  "focus-blue transition-colors",
                  "flex-1",
                )}
                aria-label={i18n.get("resetFilters") || "Reset all filters"}
              >
                {i18n.get("resetFilters")}
              </button>
            )}

            {/* Search Button */}
            <button
              type="button"
              onClick={handleSearchSubmit}
              className={cn(
                "flex items-center justify-center min-h-[48px] px-4 py-3",
                "bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover",
                "rounded-lg transition-colors focus-blue",
                "flex-1",
              )}
              aria-label={
                totalFilterCount > 0
                  ? `${i18n.get("search")} with ${totalFilterCount} ${totalFilterCount === 1 ? "filter" : "filters"} applied`
                  : i18n.get("search")
              }
            >
              <span className="label-m-medium text-white">
                {i18n.get("search")}
              </span>
            </button>
          </div>
        </>
      ) : (
        /* Saved Searches List */
        <div className="w-full bg-bg-neutral-secondary-default rounded-xl border border-border-token-brand-minimal overflow-hidden">
          {savedSearches && savedSearches.length > 0 ? (
            <div
              className="divide-y divide-primary-150"
              role="listbox"
              aria-label={i18n.get("savedSearches")}
            >
              {savedSearches.map((search) => (
                <button
                  key={search.id}
                  type="button"
                  onClick={() => handleSavedSearchSelect(search)}
                  className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-bg-neutral-secondary-hover transition-colors focus-blue"
                  role="option"
                  aria-selected={false}
                >
                  <span className="label-m-medium text-fg-neutral-strong">
                    {search.name}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <p className="label-m-medium text-fg-neutral-subtle">
                {i18n.get("noSavedSearches") || "No saved searches yet"}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Layout: Header - Sticky header with navigation
  if (layout === "header") {
    // Show skeleton while loading
    if (isLoading) {
      return (
        <header className="sticky top-0 z-[75] bg-surface-primary">
          <div className="max-w-[1280px] mx-auto relative">
            <SearchBarSkeleton />
          </div>
        </header>
      );
    }

    // Search page: input on top, filters in their own row underneath.
    if (filtersBelowInput) {
      return (
        <div
          ref={containerRef}
          // Figma header spec: 16px between the 48px input and the 32px chip row.
          className={cn("flex flex-col items-center gap-4", containerClassName)}
        >
          {/* Search field — inset so the filter row reaches past it on both sides */}
          <div className="flex items-center self-stretch mx-8 bg-bg-neutral-secondary-default border border-border-token-neutral-subtle rounded-lg h-12">
            <div className="flex-1 min-w-0 pl-4 pr-1">
              <AutoSuggestInput
                label={i18n.get("searchVenueOrLocation")}
                value={searchTerm}
                onValueChange={handleSearchChange}
                onSubmit={() => {
                  closeAllPanels();
                  handleSearchSubmit();
                }}
                onSelectUnified={(item, sessionToken) => {
                  closeAllPanels();
                  handleUnifiedSelect(item, sessionToken);
                }}
                onClear={() => {
                  handleClearSearch();
                }}
                latitude={getSafeCoordinates().lat}
                longitude={getSafeCoordinates().lng}
                radiusKm={radiusKm}
                tagging_mode={tagging_mode}
                types={types}
                accessibilities={accessibilities}
                accessibilitySections={accessibilitySections}
                showVenueOptions={showVenueOptions}
                showSearchSuggestions={showSearchSuggestions}
                onNearbyPlacesClick={handleNearbyPlacesClick}
                onRecentSearchSelect={handleRecentSearchSelect}
                onNeighborhoodSelect={handleNeighborhoodSelect}
                onLoginPress={handleLoginPress}
                suppressDropdown={false}
                className="w-full"
              />
            </div>

            <button
              type="button"
              onClick={handleSearchSubmit}
              className="flex items-center justify-center min-h-[32px] min-w-[32px] p-1.5 ml-1 mr-2 bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover rounded-lg transition-colors focus-blue"
              aria-label={i18n.get("search")}
            >
              <MagnifyingGlassIcon size={20} className="text-white" />
            </button>
          </div>

          {/* Filter row — its own width, centred on the same line as the field.
              Outer wrapper stretches to the field's width so the chip group
              (which sizes to its own content) centres within it; the inner
              row stays shrink-to-fit so "Clear all" can keep anchoring off
              its right edge via left-full. */}
          <div className="relative flex items-center justify-center self-stretch">
            <div className="relative flex items-center gap-2">
              {/* Accessibility */}
              <div className="relative">
                <FilterChip
                  ref={accessibilityButtonRef}
                  label={i18n.get("accessibility")}
                  count={computedAccessibilityCount}
                  isOpen={activePanel === "accessibility"}
                  onClick={handleAccessibilityClick}
                  onMouseDown={() => {
                    accessibilityWasOpenRef.current =
                      activePanel === "accessibility";
                  }}
                  ariaLabel={
                    computedAccessibilityCount > 0
                      ? `${i18n.get("accessibility")}, ${computedAccessibilityCount} ${computedAccessibilityCount === 1 ? "filter" : "filters"} applied`
                      : `${i18n.get("accessibility")} Filter`
                  }
                />

                {activePanel === "accessibility" && (
                  <div className="absolute left-0 top-full mt-2 z-50">
                    <AccessibilityFilterPanel
                      isOpen={true}
                      onClose={() => dismissFilterPanel("accessibility")}
                      selectedOptions={currentAccessibilityOptions}
                      onOptionsChange={handleAccessibilityOptionsChange}
                      onClearAll={handleAccessibilityClearAndSubmit}
                      onApply={handleSearchSubmit}
                      accessibilityTagsData={filterSectionsData ?? accessibilityTagsData}
                    />
                  </div>
                )}
              </div>

              {/* Business type */}
              <div className="relative" onBlur={handleFilterPanelBlur}>
                <FilterChip
                  ref={businessTypeButtonRef}
                  label={i18n.get("businessType")}
                  count={computedBusinessTypeCount}
                  isOpen={activePanel === "businessType"}
                  onClick={handleBusinessTypeClick}
                  onMouseDown={() => {
                    businessTypeWasOpenRef.current =
                      activePanel === "businessType";
                  }}
                  ariaLabel={
                    computedBusinessTypeCount > 0
                      ? `${i18n.get("businessType")}, ${computedBusinessTypeCount} ${computedBusinessTypeCount === 1 ? "filter" : "filters"} applied`
                      : `${i18n.get("businessType")} Filter`
                  }
                />

                {activePanel === "businessType" && (
                  <div className="absolute left-0 top-full mt-2 z-50">
                    <BusinessTypeDropdown
                      isOpen={true}
                      onClose={() => dismissFilterPanel("business_type")}
                      selectedTypes={currentSelectedBusinessTypes}
                      onSelectionChange={handleBusinessTypesChange}
                      onTypeToggle={handleBusinessTypeToggle}
                    />
                  </div>
                )}
              </div>

              {/* Opening hours */}
              <div className="relative" onBlur={handleFilterPanelBlur}>
                <FilterChip
                  ref={openingHoursButtonRef}
                  label={i18n.get("openingHours")}
                  count={currentOpeningHoursMode !== "any" ? 1 : 0}
                  isOpen={activePanel === "openingHours"}
                  onClick={handleOpeningHourClick}
                  onMouseDown={() => {
                    openingHoursWasOpenRef.current =
                      activePanel === "openingHours";
                  }}
                  ariaLabel={i18n.get("openingHours")}
                />

                {activePanel === "openingHours" && (
                  <div className="absolute left-0 top-full mt-2 z-50">
                    <OpeningHoursFilterPanel
                      isOpen={true}
                      onClose={() => dismissFilterPanel("opening_hours")}
                      selectedMode={currentOpeningHoursMode}
                      onModeChange={handleOpeningHoursModeChange}
                      selectedDay={currentSelectedDay}
                      onDayChange={handleDayChange}
                      selectedTime={currentSelectedTime}
                      onTimeChange={handleTimeChange}
                    />
                  </div>
                )}
              </div>

              {/* Sits outside the row's width so appearing can't shift the chips */}
              {totalFilterCount > 0 && (
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="absolute left-full ml-2 h-10 px-2 label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline underline-offset-2 focus-blue whitespace-nowrap"
                >
                  {i18n.get("clearAll")}
                </button>
              )}
            </div>
          </div>

          {statusMessage && (
            <span className="sr-only" role="status" aria-live="polite">
              {statusMessage}
            </span>
          )}
        </div>
      );
    }

    /*
     * Home's field - @see `simplified`. A separate tree rather than five
     * conditionals threaded through the markup below, which the results page
     * also renders: the two have nothing in common but the input, and the
     * results header is the last thing that should break over a home change.
     */
    if (simplified) {
      return (
        <div className="w-full">
          {/* The frame's 48px field (`15969:123996`). The input row inside
              is already 48px with 12px of its own inset, so this adds no
              vertical padding and only 3px on the left - padding here as well
              made the field 74px tall with the text 28px in. 3px and 15px, not
              4 and 16, because Figma measures its 16px from the outside of the
              stroke and CSS measures padding inside the 1px border. */}
          <div
            data-tour-anchor={tourAnchor}
            className="flex items-center gap-4 w-full h-12 pl-[3px] pr-[15px]
              bg-bg-neutral-secondary-default border border-border-token-neutral-subtle
              rounded-lg"
          >
            <div className="flex-1 min-w-0">
              <AutoSuggestInput
                label={i18n.get("searchVenueOrLocation")}
                value={searchTerm}
                onValueChange={handleSearchChange}
                onSubmit={() => {
                  closeAllPanels();
                  void runBusy(handleSearchSubmit);
                }}
                onSelectUnified={(item, sessionToken) => {
                  closeAllPanels();
                  void runBusy(() => handleUnifiedSelect(item, sessionToken));
                }}
                onClear={() => {
                  handleClearSearch();
                }}
                latitude={getSafeCoordinates().lat}
                longitude={getSafeCoordinates().lng}
                radiusKm={radiusKm}
                tagging_mode={tagging_mode}
                types={types}
                accessibilities={accessibilities}
                accessibilitySections={accessibilitySections}
                showVenueOptions={showVenueOptions}
                showSearchSuggestions={showSearchSuggestions}
                onNearbyPlacesClick={handleNearbyPlacesClick}
                onRecentSearchSelect={handleRecentSearchSelect}
                onNeighborhoodSelect={handleNeighborhoodSelect}
                onLoginPress={handleLoginPress}
                suppressDropdown={false}
                inlineSuggestionsContainer={inlineSuggestionsContainer}
                className="w-full"
              />
            </div>

            {/* The frame draws a plain glyph where the results header has a
                filled button. Still a button: it submits, and an icon that
                looks pressable should be reachable by keyboard. */}
            <button
              type="button"
              onClick={() => void runBusy(handleSearchSubmit)}
              className="flex shrink-0 items-center justify-center size-5 focus-blue
                text-fg-neutral-subtle hover:text-fg-neutral-strong transition-colors"
              aria-label={i18n.get("search")}
            >
              <MagnifyingGlassIcon size={20} />
            </button>
          </div>

          {statusMessage && (
            <span className="sr-only" role="status" aria-live="polite">
              {statusMessage}
            </span>
          )}
        </div>
      );
    }

    return (
      <header className="sticky top-0 z-[75] bg-surface-primary">
        <div className="max-w-[1280px] mx-auto relative">
          <div
            ref={containerRef}
            className={cn(
              "relative flex flex-col gap-3 items-start justify-center px-4 md:px-6 lg:px-[100px] pt-6 w-full max-w-[1280px] mx-auto",
              containerClassName,
            )}
          >
            {/* Location Text Row */}
            <div className="flex items-center gap-2 px-4 w-full">
              <SearchLocation
                locationText={locationText}
                onChangeLocationClick={
                  onLocationChange ? handleOpenChangeLocationModal : undefined
                }
                isDefaultLocation={isDefaultLocation}
                onUseCurrentLocation={handleUsePreciseLocationClickInternal}
                locationButtonRef={locationButtonRef}
                variant={locationVariant}
                citySubtitle={locationSubtitle}
              />
            </div>

            {/* Search Bar Container */}
            <div
              data-tour-anchor={tourAnchor}
              className="flex-1 flex items-center bg-bg-neutral-secondary-default border border-border-token-neutral-subtle rounded-lg min-h-[48px] w-full"
            >
              {/* Search Input */}
              <div className="flex-1 min-w-0 px-4">
                <AutoSuggestInput
                  label={i18n.get("searchVenueOrLocation")}
                  value={searchTerm}
                  onValueChange={handleSearchChange}
                  onSubmit={() => {
                    closeAllPanels();
                    handleSearchSubmit();
                  }}
                  onSelectUnified={(item, sessionToken) => {
                    closeAllPanels();
                    handleUnifiedSelect(item, sessionToken);
                  }}
                  onClear={() => {
                    handleClearSearch();
                  }}
                  latitude={getSafeCoordinates().lat}
                  longitude={getSafeCoordinates().lng}
                  radiusKm={radiusKm}
                  tagging_mode={tagging_mode}
                  types={types}
                  accessibilities={accessibilities}
                  accessibilitySections={accessibilitySections}
                  showVenueOptions={showVenueOptions}
                  showSearchSuggestions={showSearchSuggestions}
                  onNearbyPlacesClick={handleNearbyPlacesClick}
                  onRecentSearchSelect={handleRecentSearchSelect}
                  onNeighborhoodSelect={handleNeighborhoodSelect}
                  onLoginPress={handleLoginPress}
                  suppressDropdown={false}
                  className="w-full"
                />
              </div>

              {/* Filters Section */}
              <div className="flex items-center">
                {/* Divider */}
                <div
                  className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
                  aria-hidden="true"
                />

                {/* Accessibility Filter */}
                <div className="relative pl-2 pr-2">
                  <button
                    ref={accessibilityButtonRef}
                    type="button"
                    onClick={handleAccessibilityClick}
                    onMouseDown={() => {
                      accessibilityWasOpenRef.current =
                        activePanel === "accessibility";
                    }}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 transition-colors rounded-[8px] focus-blue",
                      "bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover",
                      activePanel === "accessibility" ||
                        computedAccessibilityCount > 0
                        ? "bg-bg-brand-ghost-active"
                        : "",
                    )}
                    aria-label={
                      computedAccessibilityCount > 0
                        ? `${i18n.get("accessibility")}, ${computedAccessibilityCount} ${computedAccessibilityCount === 1 ? "filter" : "filters"} applied`
                        : `${i18n.get("accessibility")} Filter`
                    }
                    aria-expanded={activePanel === "accessibility"}
                    aria-haspopup="dialog"
                  >
                    <span className="label-m-medium text-fg-neutral-strong hover:text-fg-neutral-strong whitespace-nowrap">
                      {i18n.get("accessibility")}
                    </span>
                    {computedAccessibilityCount > 0 ? (
                      <span
                        className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                        aria-hidden="true"
                      >
                        {computedAccessibilityCount}
                      </span>
                    ) : (
                      <ChevronDown
                        size={16}
                        className={cn(
                          "text-fg-neutral-subtle transition-transform",
                          activePanel === "accessibility" && "rotate-180",
                        )}
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {activePanel === "accessibility" && (
                    // <>
                    //   {/* Backdrop to prevent parent scroll and interactions */}
                    //   <div
                    //     className="fixed inset-0 bg-black/20 z-40"
                    //     onClick={closeAllPanels}
                    //     aria-hidden="true"
                    //   />
                    <div className="absolute left-0 top-full mt-2 z-50">
                      <AccessibilityFilterPanel
                        isOpen={true}
                        onClose={() => dismissFilterPanel("accessibility")}
                        selectedOptions={currentAccessibilityOptions}
                        onOptionsChange={handleAccessibilityOptionsChange}
                        onClearAll={handleAccessibilityClearAll}
                        accessibilityTagsData={filterSectionsData ?? accessibilityTagsData}
                      />
                    </div>
                    // </>
                  )}
                </div>

                {/* Divider */}
                <div
                  className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
                  aria-hidden="true"
                />

                {/* Business Type Filter */}
                <div
                  className="relative  pl-2 pr-2"
                  onBlur={handleFilterPanelBlur}
                >
                  <button
                    ref={businessTypeButtonRef}
                    type="button"
                    onClick={handleBusinessTypeClick}
                    onMouseDown={() => {
                      businessTypeWasOpenRef.current =
                        activePanel === "businessType";
                    }}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 transition-colors rounded-[8px] focus-blue",
                      "bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover",
                      activePanel === "businessType" ||
                        computedBusinessTypeCount > 0
                        ? "bg-bg-brand-ghost-active"
                        : "",
                    )}
                    aria-label={
                      computedBusinessTypeCount > 0
                        ? `${i18n.get("businessType")}, ${computedBusinessTypeCount} ${computedBusinessTypeCount === 1 ? "filter" : "filters"} applied`
                        : `${i18n.get("businessType")} Filter`
                    }
                    aria-expanded={activePanel === "businessType"}
                    aria-haspopup="listbox"
                  >
                    <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                      {i18n.get("businessType")}
                    </span>
                    {computedBusinessTypeCount > 0 ? (
                      <span
                        className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                        aria-hidden="true"
                      >
                        {computedBusinessTypeCount}
                      </span>
                    ) : (
                      <ChevronDown
                        size={16}
                        className={cn(
                          "text-fg-neutral-subtle transition-transform",
                          activePanel === "businessType" && "rotate-180",
                        )}
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {activePanel === "businessType" && (
                    <div className="absolute left-0 top-full mt-2 z-50">
                      <BusinessTypeDropdown
                        isOpen={true}
                        onClose={() => dismissFilterPanel("business_type")}
                        businessTypes={businessTypes}
                        selectedTypes={currentSelectedBusinessTypes}
                        onSelectionChange={handleBusinessTypesChange}
                        onTypeToggle={handleBusinessTypeToggle}
                        onClearAll={() =>
                          trackEvent("filter_cleared", {
                            filters_count: buildFilterSummary().filters_count,
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div
                  className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
                  aria-hidden="true"
                />

                {/* Opening Hour Filter */}
                <div
                  className="relative  pl-2 pr-2"
                  onBlur={handleFilterPanelBlur}
                >
                  <button
                    ref={openingHoursButtonRef}
                    type="button"
                    onClick={handleOpeningHourClick}
                    onMouseDown={() => {
                      openingHoursWasOpenRef.current =
                        activePanel === "openingHours";
                    }}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 transition-colors rounded-[8px] focus-blue",
                      "bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover",
                      activePanel === "openingHours" ||
                        (currentOpeningHoursMode !== "any" &&
                          currentOpeningHoursMode !== null)
                        ? "bg-bg-brand-ghost-active"
                        : "",
                    )}
                    aria-label={
                      currentOpeningHoursMode !== "any"
                        ? `${i18n.get("openingHours")}, 1 filter applied`
                        : `${i18n.get("openingHours")} Filter`
                    }
                    aria-expanded={activePanel === "openingHours"}
                    aria-haspopup="dialog"
                  >
                    <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                      {i18n.get("openingHours")}
                    </span>
                    {currentOpeningHoursMode !== "any" ? (
                      <span
                        className="flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                        aria-hidden="true"
                      >
                        1
                      </span>
                    ) : (
                      <ChevronDown
                        size={16}
                        className={cn(
                          "text-fg-neutral-subtle transition-transform",
                          activePanel === "openingHours" && "rotate-180",
                        )}
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {activePanel === "openingHours" && (
                    <div className="absolute right-0 top-full mt-2 z-50">
                      <OpeningHoursFilterPanel
                        isOpen={true}
                        onClose={() => dismissFilterPanel("opening_hours")}
                        selectedMode={currentOpeningHoursMode}
                        onModeChange={handleOpeningHoursModeChange}
                        selectedDay={currentSelectedDay}
                        onDayChange={handleDayChange}
                        selectedTime={currentSelectedTime}
                        onTimeChange={handleTimeChange}
                      />
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div
                  className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
                  aria-hidden="true"
                />

                {/* Search Button - Now inside the container */}
                <button
                  type="button"
                  onClick={handleSearchSubmit}
                  className="flex items-center justify-center min-h-[32px] min-w-[32px] p-1.5 mx-2 bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover rounded-lg transition-colors focus-blue"
                  aria-label={i18n.get("search")}
                >
                  <MagnifyingGlassIcon size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* Bottom Row: Saved Search + Recent Searches */}
            <div className="flex items-center gap-3 w-full">
              {/* Saved Searches Combobox */}
              {savedSearchOptions.length >= 0 && (
                <Combobox
                  options={savedSearchOptions}
                  value=""
                  onValueChange={handleSavedSearchSelectFromCombobox}
                  placeholder={`${i18n.get("applySavedSearches")} (${savedSearchesCount})`}
                  ariaLabel={`${i18n.get("applySavedSearches")} (${savedSearchesCount})`}
                  emptyText={
                    i18n.get("noSavedSearches") || "No saved searches yet"
                  }
                  showSearch={false}
                  searchPlaceholder={i18n.get("search") || "Search..."}
                  className="w-full md:w-[250px]"
                  showBorderOnTrigger={false}
                />
              )}

              {/* Divider */}
              {recentSearches.length > 0 && (
                <div
                  className="h-[24px] w-px border border-border-token-neutral-minimal"
                  aria-hidden="true"
                />
              )}

              {/* Recent Searches Section */}
              <div className="flex items-center gap-3 flex-1 overflow-hidden">
                {!isLoggedIn ? (
                  <button
                    type="button"
                    onClick={onLoginPress}
                    className="label-m-medium text-fg-neutral-strong hover:opacity-80 transition-opacity focus-blue whitespace-nowrap"
                  >
                    {i18n.get("loginForYourRecentSearch")}
                  </button>
                ) : recentSearches.length > 0 ? (
                  <>
                    <span className="label-m-medium text-fg-neutral-strong whitespace-nowrap">
                      {i18n.get("recentSearches")}
                    </span>

                    <ScrollContainer className="flex items-center gap-3 p-1">
                      {recentSearches.slice(0, 5).map((search, index) => (
                        <button
                          key={search.id || index}
                          ref={(el) => {
                            if (el) recentSearchRefs.current[index] = el;
                          }}
                          type="button"
                          onClick={() => handleRecentSearchSelect(search)}
                          onKeyDown={(e) =>
                            handleRecentSearchKeyDown(
                              e,
                              index,
                              Math.min(recentSearches.length, 5),
                            )
                          }
                          className="
                          flex items-center gap-1.5 px-3 py-2 transition-colors rounded-[8px] focus-blue flex-shrink-0
                          whitespace-nowrap 
                          justify-center 
                          min-h-[40px]
                          label-m-medium 
                          text-fg-neutral-strong 
                          bg-bg-brand-secondary-default 
                          border border-border-token-brand-minimal                                                    
                          hover:bg-bg-brand-secondary-hover 
                          active:bg-bg-brand-secondary-active
                          "
                        >
                          <span className="label-m-medium text-fg-neutral-strong">
                            {search.search_term.charAt(0).toUpperCase() +
                              search.search_term.slice(1)}
                          </span>
                        </button>
                      ))}
                    </ScrollContainer>
                  </>
                ) : (
                  <span className="label-m-medium text-fg-neutral-subtle whitespace-nowrap">
                    {i18n.get("noRecentSearches")}
                  </span>
                )}
              </div>
            </div>

            {/* Change Location Modal */}
            {onLocationChange && isChangeLocationModalOpen && (
              <ChangeLocationModal
                isOpen={isChangeLocationModalOpen}
                onClose={handleCloseChangeLocationModal}
                onCurrentLocationPress={handleUsePreciseLocationClickInternal}
                onLocationSelect={handleLocationSelect}
                latitude={latitude}
                longitude={longitude}
                onSearchSelect={handleUnifiedSelect}
              />
            )}
          </div>
        </div>
      </header>
    );
  }

  // Layout: OverlayMobile - Full-screen left sheet for mobile
  if (layout === "overlaymobile") {
    return (
      <Sheet
        open={isOverlayOpen}
        onOpenChange={(open) => {
          if (!open) {
            onOverlayClose?.();
          }
        }}
      >
        <SheetContent
          side="left"
          showClose={false}
          className={cn(
            "w-full sm:max-w-full",
            "bg-bg-neutral-secondary-default",
            "overflow-y-auto",
            "z-1500",
          )}
        >
          {/* Visually hidden title for screen readers */}
          <SheetTitle className="sr-only">
            {i18n.get("searchOverlay")}
          </SheetTitle>

          {/* Close button â€” top-left of sheet */}
          <div className="flex items-center px-4 pt-3 md:px-6">
            <button
              type="button"
              onClick={() => {
                onOverlayClose?.();
              }}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md focus-blue text-fg-neutral-strong"
              aria-label={i18n.get("close")}
            >
              <ArrowLeftIcon size={20} className="text-fg-brand-subtle" />
              <span className="label-m-medium text-fg-brand-subtle ml-2">
                {i18n.get("back") || "Back"}
              </span>
            </button>
          </div>

          {/* Search Content */}
          <section className="w-full max-w-[1440px] mx-auto bg-bg-neutral-secondary-default pb-6 pt-3 px-4 md:px-6">
            <div
              ref={containerRef}
              className={cn("relative flex flex-col gap-3", containerClassName)}
            >
              {contentMobile}
            </div>
          </section>
        </SheetContent>
      </Sheet>
    );
  }

  // Layout: Overlay - Modal with backdrop
  if (layout === "overlay") {
    if (!isOverlayOpen) return null;

    return (
      <>
        {/* Backdrop - positioned below navbar */}
        <div
          className="fixed inset-0 top-[80px] bg-black/30 z-[100]"
          onClick={() => {
            onOverlayClose();
          }}
          aria-hidden="true"
        />

        {/* Overlay Panel - positioned below navbar */}
        <div
          className={cn(
            "fixed top-[80px] left-0 right-0 z-[101]",
            "bg-bg-neutral-secondary-default",
            "shadow-lg rounded-b-xl",
          )}
          role="dialog"
          aria-modal="true"
          aria-label={i18n.get("searchOverlay")}
        >
          {/* Search Content */}
          <section className="w-full max-w-[1440px] mx-auto bg-bg-neutral-secondary-default pb-6 px-4 md:px-6">
            <div
              ref={containerRef}
              className={cn("relative flex flex-col gap-3", containerClassName)}
            >
              {content}
            </div>
          </section>
        </div>
      </>
    );
  }

  // Layout: Default - Original behavior
  if (!showContainer) {
    return (
      <div ref={containerRef} className={cn("relative", containerClassName)}>
        {content}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex flex-col gap-3 items-start justify-center px-4 md:px-6 lg:px-48 py-6 md:py-10 lg:py-[50px] w-full max-w-[1280px] mx-auto",
        containerClassName,
      )}
    >
      {content}
    </div>
  );
};

// ============================================================================
// Skeleton Component
// ============================================================================

export const SearchBarSkeleton = () => {
  return (
    <div className="flex flex-col gap-3 items-start justify-center px-4 md:px-6 lg:px-48 py-6 md:py-10 lg:py-[50px] w-full max-w-[1280px] mx-auto">
      {/* Saved searches toggle skeleton */}
      <div className="h-6 w-40 rounded bg-container-brand-minimal animate-pulse" />

      {/* Search bar container skeleton */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-6 items-stretch md:items-center w-full">
        <div className="flex-1 h-[48px] md:h-[56px] rounded-lg bg-container-brand-minimal animate-pulse" />
        <div className="h-[44px] md:h-[56px] w-full md:w-[100px] rounded-lg bg-container-brand-minimal animate-pulse" />
      </div>
    </div>
  );
};

export default SearchBar;
